import { useState, useEffect, useRef } from 'react';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from './supabase';

function logAdminUi(tag, ...args) {
  try {
    const parts = args.filter((a) => a !== undefined && a !== null && a !== '');
    if (parts.length) console.warn('[admin-ui]', tag, ...parts);
  } catch { /* ignore */ }
}

// ── Edge Function 호출 헬퍼 ──────────────────
// PIN은 서버(Edge Function)에서만 검증 → service_role 키는 절대 프론트에 없음
const adminApi = async (pin, action, data = {}) => {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { 'x-admin-pin': pin };
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }
  const { data: result, error } = await supabase.functions.invoke('admin-api', {
    body: { action, data },
    headers,
  });
  if (error) {
    logAdminUi('invoke', action, error?.message, error);
    if (error instanceof FunctionsHttpError) {
      try {
        const body = await error.context.json();
        logAdminUi('invoke http body', action, body);
        if (body && typeof body.error === 'string' && body.error.trim()) {
          let msg = body.error.trim();
          if (typeof body.detail === 'string' && body.detail.trim()) {
            msg = `${msg}\n${body.detail.trim()}`;
          }
          throw new Error(msg);
        }
      } catch (inner) {
        if (!(inner instanceof SyntaxError)) throw inner;
      }
    }
    throw new Error('관리 요청이 실패했습니다. 네트워크·PIN을 확인하거나 잠시 후 다시 시도해주세요.');
  }
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    if ('success' in result && result.success === false && result.error) {
      throw new Error(String(result.error));
    }
    if ('error' in result && !('success' in result)) {
      throw new Error(String(result.error));
    }
  }
  return result;
};

const HOBBY_OPTIONS = [
  { val:'wine',    label:'🍷 와인·맛집' },
  { val:'brunch',  label:'☕ 브런치·카페' },
  { val:'book',    label:'📚 책·독서' },
  { val:'culture', label:'🎨 전시·문화생활' },
  { val:'walk',    label:'🚶 산책·등산' },
  { val:'game',    label:'🎮 게임·오락' },
  { val:'sports',  label:'⚽ 운동·스포츠' },
  { val:'other',   label:'🧩 기타' },
];
const HOBBY_MAP   = Object.fromEntries(HOBBY_OPTIONS.map(h => [h.val, h.label]));
const TIME_OPTIONS = [
  { val:'weekday_morning', label:'평일 오전' },
  { val:'weekday_evening', label:'평일 저녁' },
  { val:'weekend_day',     label:'주말 낮' },
  { val:'weekend_evening', label:'주말 저녁' },
];
const AGE_OPTIONS = ['all'];
const getRoomOpenMinMembers = (club) => {
  const createdAtMs = club?.created_at ? new Date(club.created_at).getTime() : NaN;
  if (!Number.isFinite(createdAtMs)) return 6;
  const hours = (Date.now() - createdAtMs) / 3600000;
  return hours < 24 ? 6 : 4;
};
const isClubOpenForChat = (club) => {
  if (!club) return false;
  if (club.status === 'active') return true;
  return (club.members || 0) >= getRoomOpenMinMembers(club);
};

const S = {
  navy:'#1A2B3C', green:'#2E9B64', greenLight:'#E8F5EE',
  red:'#EF4444',  redLight:'#FEF2F2',
  yellow:'#F59E0B', yellowLight:'#FEF3C7',
  blue:'#3B82F6',   blueLight:'#EFF6FF',
  gray50:'#F9FAFB', gray100:'#F3F4F6', gray200:'#E5E7EB',
  gray400:'#9CA3AF', gray600:'#6B7280', gray900:'#111827', white:'#FFFFFF',
};

/** 사이드바 ‘신규’ 배지: 모임방·유저는 마지막으로 해당 탭을 연 시점(max created_at) 이후만 카운트 */
const ADMIN_NAV_BADGE_SEEN_KEY = 'admin_nav_badge_seen_v1';
function loadAdminNavBadgeSeen() {
  try {
    const o = JSON.parse(localStorage.getItem(ADMIN_NAV_BADGE_SEEN_KEY) || '{}');
    return o && typeof o === 'object' ? o : {};
  } catch {
    return {};
  }
}
function saveAdminNavBadgeSeen(obj) {
  try {
    localStorage.setItem(ADMIN_NAV_BADGE_SEEN_KEY, JSON.stringify(obj));
  } catch { /* ignore */ }
}

export default function AdminPage() {
  const [authed, setAuthed]         = useState(false);
  const [verifiedPin, setVerifiedPin] = useState('');
  const [pin, setPin]               = useState('');
  const [pinError, setPinError]     = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  const [tab, setTab]               = useState('dashboard');
  const [loading, setLoading]       = useState(false);

  // 데이터
  const [clubs, setClubs]     = useState([]);
  const [reports, setReports] = useState([]);
  const [warningCountByUserId] = useState({});
  const [, setKickedByClubUser] = useState({});
  const [, setKickedReportIds] = useState({});
  const [hostClubKicks, setHostClubKicks] = useState([]);
  const [hostKickRiskByKey, setHostKickRiskByKey] = useState({});
  const [hostKickDoneByKey, setHostKickDoneByKey] = useState({});
  const tabRef = useRef('dashboard');
  /** 사이드바에서 해당 탭 클릭 후 reload 끝나면 ‘읽음’ 스냅샷 저장 */
  const pendingNavAckRef = useRef(null);
  const [notices, setNotices] = useState([]);
  const [users, setUsers]     = useState([]);
  const [banned, setBanned]   = useState([]);
  const [accountSuspendedUsersV2, setAccountSuspendedUsersV2] = useState([]);
  const [sanctionReleaseLogs, setSanctionReleaseLogs] = useState([]);
  const [coinUsers, setCoinUsers] = useState([]);
  const [coinInputs, setCoinInputs] = useState({});
  const [chatBannedUsers, setChatBannedUsers] = useState([]);
  const [roomKickedUsers, setRoomKickedUsers] = useState([]);
  const [accountSuspendedUsers, setAccountSuspendedUsers] = useState([]);
  const [profanityRiskUsers, setProfanityRiskUsers] = useState([]);
  const [illegalRiskUsers, setIllegalRiskUsers] = useState([]);
  const [illegalKeywordsList, setIllegalKeywordsList] = useState([]);
  const [newIllegalPhrase, setNewIllegalPhrase] = useState('');
  const [adminActions, setAdminActions] = useState([]);
  const [resetProfanityModal] = useState({ open: false, row: null });
  const [sanctionReviews] = useState([]);
  const [clubSearch, setClubSearch] = useState('');
  const [navBadgeSeen, setNavBadgeSeen] = useState(() => loadAdminNavBadgeSeen());

  const renderUserNameOnly = (userId, profile) => {
    const fromProfile = String(profile?.nickname || profile?.name || '').trim();
    if (fromProfile) return fromProfile;
    const fromUsers = String(displayUserById(userId) || '').trim();
    if (fromUsers && !/^[0-9a-f]{8}-/i.test(fromUsers)) return fromUsers;
    return '이름없음';
  };

  // 클럽 생성 폼
  const [showCreateClub, setShowCreateClub] = useState(false);
  const [newClub, setNewClub] = useState({ hobby:'wine', region:'서울', age_group:'all', ratio:'mixed', times:[], max_members:6, host_id:'' });

  // 공지사항 폼
  const [noticeTitle,   setNoticeTitle]   = useState('');
  const [noticeContent, setNoticeContent] = useState('');
  const [noticeTarget,  setNoticeTarget]  = useState('전체');

  // UI
  const [notification, setNotification] = useState(null);
  const refreshTimersRef = useRef({});
  const HOST_KICK_DONE_LS_KEY = 'admin_host_kick_done_keys_v1';

  tabRef.current = tab;

  const showNotif = (msg, type='success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(HOST_KICK_DONE_LS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      setHostKickDoneByKey(parsed && typeof parsed === 'object' ? parsed : {});
    } catch {
      setHostKickDoneByKey({});
    }
  }, []);

  const markHostKickDone = (row) => {
    const key = `${String(row?.club_id || '').trim()}::${String(row?.user_id || '').trim()}`;
    if (!key || key === '::') return;
    setHostKickDoneByKey((prev) => {
      const next = { ...prev, [key]: true };
      try { localStorage.setItem(HOST_KICK_DONE_LS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
    showNotif('처리완료로 표시했습니다.');
  };

  const hydrateClubsWithMemberCounts = async (rows) => {
    const clubsRows = rows || [];
    if (clubsRows.length === 0) return clubsRows;
    const ids = clubsRows.map(c => String(c?.id || '').trim()).filter(Boolean);
    if (ids.length === 0) return clubsRows;
    const { data: memberRows, error } = await supabase
      .from('club_members')
      .select('club_id')
      .in('club_id', ids);
    if (error) {
      console.warn('admin clubs member count hydrate failed:', error.message);
      return clubsRows;
    }
    const countByClub = {};
    (memberRows || []).forEach(m => {
      const key = String(m?.club_id || '').trim();
      if (!key) return;
      countByClub[key] = (countByClub[key] || 0) + 1;
    });
    return clubsRows.map(c => ({
      ...c,
      members: Number.isFinite(countByClub[String(c?.id || '').trim()])
        ? countByClub[String(c?.id || '').trim()]
        : (c.members || 0),
    }));
  };

  // ── 데이터 로드 ─────────────────────────────
  const reload = async (section = 'all') => {
    if (!verifiedPin) return;
    setLoading(true);
    let loadedReports;
    let loadedSanctionLogs;
    let loadedSuspendedV2;
    let loadedProfanity;
    let loadedIllegal;
    let loadedClubsForAck;
    let loadedUsersForAck;
    try {
      if (section === 'all' || section === 'clubs') {
        // 직접 DB 쿼리 → Edge Function 필터에 무관하게 active 방도 그대로 표시
        const { data: directClubs } = await supabase
          .from('clubs').select('*')
          .eq('is_active', true)
          .neq('status', 'cancelled')
          .order('created_at', { ascending: false });
        loadedClubsForAck = await hydrateClubsWithMemberCounts(directClubs || []);
        setClubs(loadedClubsForAck);
      }
      if (section === 'all' || section === 'reports' || section === 'host_kicks') {
        if (section === 'all' || section === 'reports') {
          loadedReports = await adminApi(verifiedPin, 'get_reports_v2', { include_done: true }) || [];
          setReports(loadedReports);
        }
        if (section === 'all' || section === 'host_kicks') {
          setHostKickRiskByKey({});
          setHostClubKicks([]);
        }
      }
      if (section === 'all' || section === 'notices') setNotices(await adminApi(verifiedPin, 'get_notices') || []);
      if (section === 'all' || section === 'users') {
        loadedUsersForAck = await adminApi(verifiedPin, 'get_users') || [];
        setUsers(loadedUsersForAck);
      }
      if (section === 'all' || section === 'banned')  setBanned(await adminApi(verifiedPin, 'get_banned_users_v2') || []);
      if (section === 'all' || section === 'sanction_release_logs') {
        try {
          loadedSanctionLogs = await adminApi(verifiedPin, 'get_sanction_release_logs_v2') || [];
          setSanctionReleaseLogs(loadedSanctionLogs);
        } catch (e) {
          logAdminUi('reload sanction_release_logs', e?.message, e);
          loadedSanctionLogs = [];
          setSanctionReleaseLogs([]);
        }
      }
      if (section === 'all' || section === 'chat_bans') setChatBannedUsers([]);
      if (section === 'all' || section === 'room_kicks_manage') setRoomKickedUsers([]);
      if (section === 'all' || section === 'account_suspensions') setAccountSuspendedUsers([]);
      if (section === 'all' || section === 'banned') {
        try {
          loadedSuspendedV2 = await adminApi(verifiedPin, 'get_account_suspended_users_v2') || [];
          setAccountSuspendedUsersV2(loadedSuspendedV2);
        } catch (e) {
          logAdminUi('reload accountSuspendedUsersV2', e?.message, e);
          loadedSuspendedV2 = [];
          setAccountSuspendedUsersV2([]);
        }
      }
      if (section === 'all' || section === 'profanity_risk') {
        try {
          loadedProfanity = await adminApi(verifiedPin, 'get_profanity_risk_users') || [];
          setProfanityRiskUsers(loadedProfanity);
        } catch (e) {
          logAdminUi('reload profanity_risk', e?.message, e);
          loadedProfanity = [];
          setProfanityRiskUsers([]);
        }
      }
      if (section === 'all' || section === 'illegal_users') {
        try {
          loadedIllegal = await adminApi(verifiedPin, 'get_illegal_risk_users') || [];
          setIllegalRiskUsers(loadedIllegal);
          setIllegalKeywordsList(await adminApi(verifiedPin, 'list_illegal_keywords') || []);
        } catch (e) {
          logAdminUi('reload illegal_users', e?.message, e);
          loadedIllegal = [];
          setIllegalRiskUsers([]);
          setIllegalKeywordsList([]);
        }
      }
      if (section === 'all' || section === 'admin_actions') setAdminActions([]);
      if (section === 'coins') {
        try {
          setCoinUsers(await adminApi(verifiedPin, 'get_users_coins') || []);
        } catch {
          showNotif('포인트 탭: Edge Function에 get_users_coins 액션 추가 필요', 'error');
        }
      }

      const ack = pendingNavAckRef.current;
      if (ack) {
        pendingNavAckRef.current = null;
        setNavBadgeSeen((prev) => {
          const next = { ...prev };
          if (ack === 'clubs' && loadedClubsForAck && loadedClubsForAck.length) {
            const maxT = Math.max(0, ...loadedClubsForAck.map((c) => new Date(c.created_at || 0).getTime()));
            if (maxT > 0) next.clubsMaxCreatedAt = new Date(maxT).toISOString();
          } else if (ack === 'users' && loadedUsersForAck && loadedUsersForAck.length) {
            const maxT = Math.max(0, ...loadedUsersForAck.map((u) => new Date(u.created_at || 0).getTime()));
            if (maxT > 0) next.usersMaxCreatedAt = new Date(maxT).toISOString();
          } else if (ack === 'reports' && loadedReports !== undefined) {
            const ids = loadedReports
              .filter((r) => r.status === 'pending')
              .map((r) => String(r.id || '').trim())
              .filter(Boolean);
            next.seenPendingReportIds = [...new Set(ids)];
          } else if (ack === 'profanity_risk' && loadedProfanity !== undefined) {
            const ids = loadedProfanity
              .map((u) => String(u.user_id || '').trim())
              .filter(Boolean);
            next.seenProfanityUserIds = [...new Set(ids)];
          } else if (ack === 'illegal_users' && loadedIllegal !== undefined) {
            const ids = loadedIllegal
              .map((u) => String(u.user_id || '').trim())
              .filter(Boolean);
            next.seenIllegalUserIds = [...new Set(ids)];
          } else if (ack === 'banned' && loadedSuspendedV2 !== undefined) {
            const ids = loadedSuspendedV2
              .map((r) => String(r.id || '').trim())
              .filter(Boolean);
            next.seenSuspendedSanctionIds = [...new Set(ids)];
          } else if (ack === 'sanction_release_logs' && loadedSanctionLogs !== undefined) {
            const ids = loadedSanctionLogs
              .map((r) => String(r.id || '').trim())
              .filter(Boolean);
            next.seenSanctionLogIds = [...new Set(ids)];
          }
          saveAdminNavBadgeSeen(next);
          return next;
        });
      }
    } catch (e) {
      pendingNavAckRef.current = null;
      logAdminUi('reload', e?.message, e);
      showNotif('데이터를 불러오지 못했습니다. PIN·네트워크를 확인하거나 잠시 후 다시 시도해주세요.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccountSuspensionV2 = async (row) => {
    const sid = String(row?.id || '').trim();
    const uid = String(row?.user_id || '').trim();
    const label = String(row?.display_name || uid || '사용자').trim();
    if (!window.confirm(`${label}의 정지 데이터를 삭제할까요?\n\n(정지 해제가 아니라 기록 삭제이며, banned_users(UUID)도 함께 정리됩니다)`)) return;
    try {
      const res = await adminApi(verifiedPin, 'delete_account_suspension_v2', {
        sanction_id: sid || null,
        user_id: uid || null,
      });
      if (res?.success === false) throw new Error(res?.error || 'delete_account_suspension_v2 failed');
      await reload('banned');
      showNotif('정지 데이터를 삭제했습니다.');
    } catch (e) {
      logAdminUi('handleDeleteAccountSuspensionV2', e?.message, e);
      showNotif(e?.message || '정지 데이터 삭제에 실패했습니다.', 'error');
    }
  };

  useEffect(() => { if (authed && verifiedPin) reload(); }, [authed, verifiedPin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authed) setNavBadgeSeen(loadAdminNavBadgeSeen());
  }, [authed]);

  // 첫 로드 시 기존 모임·유저는 ‘이미 봄’으로 간주해 배지 0부터 시작
  useEffect(() => {
    if (!authed) return;
    setNavBadgeSeen((prev) => {
      const next = { ...prev };
      let changed = false;
      if (!next.clubsMaxCreatedAt && clubs.length) {
        const maxT = Math.max(0, ...clubs.map((c) => new Date(c.created_at || 0).getTime()));
        if (maxT > 0) {
          next.clubsMaxCreatedAt = new Date(maxT).toISOString();
          changed = true;
        }
      }
      if (!next.usersMaxCreatedAt && users.length) {
        const maxT = Math.max(0, ...users.map((u) => new Date(u.created_at || 0).getTime()));
        if (maxT > 0) {
          next.usersMaxCreatedAt = new Date(maxT).toISOString();
          changed = true;
        }
      }
      if (changed) saveAdminNavBadgeSeen(next);
      return changed ? next : prev;
    });
  }, [authed, clubs, users]);

  // 관리자 화면도 DB 변경 즉시 자동 반영 (수동 새로고침 불필요)
  useEffect(() => {
    if (!authed || !verifiedPin) return undefined;
    const scheduleReload = (section = 'all', delay = 250) => {
      if (refreshTimersRef.current[section]) clearTimeout(refreshTimersRef.current[section]);
      refreshTimersRef.current[section] = setTimeout(() => {
        reload(section);
        refreshTimersRef.current[section] = null;
      }, delay);
    };
    const clubsChannel = supabase.channel('admin_clubs_live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'clubs' }, () => scheduleReload('clubs'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'club_members' }, () => scheduleReload('clubs'))
      .subscribe();
    const noticesChannel = supabase.channel('admin_notices_live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notices' }, () => scheduleReload('notices'))
      .subscribe();
    const usersChannel = supabase.channel('admin_users_live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        scheduleReload('users');
        scheduleReload('coins');
      })
      .subscribe();
    const reportsChannel = supabase.channel('admin_reports_v2_live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'report_cases_v2' }, () => {
        scheduleReload('reports');
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'moderation_logs' }, () => {
        // 신고 목록 카드의 최근 7일/누적 욕설 지표를 즉시 반영
        scheduleReload('reports');
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_warnings_v2' }, () => {
        scheduleReload('reports');
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_sanctions_v2' }, () => {
        scheduleReload('reports');
        scheduleReload('banned');
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_global_risk_scores' }, () => {
        scheduleReload('reports');
        scheduleReload('profanity_risk');
        scheduleReload('illegal_users');
      })
      .subscribe();
    const sanctionLogsChannel = supabase.channel('admin_sanction_release_logs_live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sanction_release_logs_v2' }, () => {
        scheduleReload('sanction_release_logs');
      })
      .subscribe();
    return () => {
      Object.values(refreshTimersRef.current).forEach(t => t && clearTimeout(t));
      refreshTimersRef.current = {};
      supabase.removeChannel(clubsChannel);
      supabase.removeChannel(noticesChannel);
      supabase.removeChannel(usersChannel);
      supabase.removeChannel(reportsChannel);
      supabase.removeChannel(sanctionLogsChannel);
    };
  }, [authed, verifiedPin]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── PIN 인증 (서버에서 검증) ──────────────────
  const handlePin = async () => {
    setPinLoading(true);
    try {
      await adminApi(pin, 'get_clubs'); // PIN 검증 겸 첫 데이터 로드
      setVerifiedPin(pin);
      setAuthed(true);
      setPinError(null);
    } catch (e) {
      logAdminUi('handlePin', e?.message, e);
      setPinError(e?.message && String(e.message).trim() ? e.message : '관리자 인증에 실패했어요');
      setPin('');
    } finally {
      setPinLoading(false);
    }
  };

  // 통계
  const stats = {
    totalClubs:      clubs.length,
    recruitingClubs: clubs.filter(c => (c.members||0) < (c.max_members||6)).length,
    totalUsers:      users.length,
    pendingReports:  reports.filter(r => r.status==='pending').length,
    activeNotices:   notices.filter(n => n.active).length,
    bannedCount:     banned.length,
    hostKickCount:   hostClubKicks.length,
  };

  const newClubNavCount = (() => {
    const t = navBadgeSeen.clubsMaxCreatedAt;
    if (!t) return 0;
    const ms = new Date(t).getTime();
    return clubs.filter((c) => new Date(c.created_at || 0).getTime() > ms).length;
  })();
  const newUserNavCount = (() => {
    const t = navBadgeSeen.usersMaxCreatedAt;
    if (!t) return 0;
    const ms = new Date(t).getTime();
    return users.filter((u) => new Date(u.created_at || 0).getTime() > ms).length;
  })();
  const seenPendingReportIds = new Set(
    Array.isArray(navBadgeSeen.seenPendingReportIds) ? navBadgeSeen.seenPendingReportIds : [],
  );
  const seenProfanityUserIds = new Set(
    Array.isArray(navBadgeSeen.seenProfanityUserIds) ? navBadgeSeen.seenProfanityUserIds : [],
  );
  const seenIllegalUserIds = new Set(
    Array.isArray(navBadgeSeen.seenIllegalUserIds) ? navBadgeSeen.seenIllegalUserIds : [],
  );
  const seenSuspendedSanctionIds = new Set(
    Array.isArray(navBadgeSeen.seenSuspendedSanctionIds) ? navBadgeSeen.seenSuspendedSanctionIds : [],
  );
  const seenSanctionLogIds = new Set(
    Array.isArray(navBadgeSeen.seenSanctionLogIds) ? navBadgeSeen.seenSanctionLogIds : [],
  );
  const reportsNavCount = reports.filter(
    (r) => r.status === 'pending' && String(r.id || '').trim() && !seenPendingReportIds.has(String(r.id).trim()),
  ).length;
  const profanityNavCount = profanityRiskUsers.filter(
    (u) => String(u.user_id || '').trim() && !seenProfanityUserIds.has(String(u.user_id).trim()),
  ).length;
  const illegalNavCount = illegalRiskUsers.filter(
    (u) => String(u.user_id || '').trim() && !seenIllegalUserIds.has(String(u.user_id).trim()),
  ).length;
  const suspendedNavCount = accountSuspendedUsersV2.filter(
    (r) => String(r.id || '').trim() && !seenSuspendedSanctionIds.has(String(r.id).trim()),
  ).length;
  const sanctionLogsNavCount = sanctionReleaseLogs.filter(
    (r) => String(r.id || '').trim() && !seenSanctionLogIds.has(String(r.id).trim()),
  ).length;

  const renderNavCountBadge = (n) => {
    const v = Number(n) || 0;
    if (v <= 0) return null;
    const text = String(v);
    return (
      <span
        style={{
          minWidth: 22,
          height: 22,
          padding: text.length > 3 ? '0 7px' : '0 6px',
          borderRadius: 11,
          background: S.red,
          color: S.white,
          fontSize: 12,
          fontWeight: 900,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {text}
      </span>
    );
  };
  const filteredClubs = clubs.filter(c => {
    const q = clubSearch.trim().toLowerCase();
    if (!q) return true;
    const title = String(c.title || '').toLowerCase();
    const hobby = String(HOBBY_MAP[c.hobby] || c.hobby || '').toLowerCase();
    return title.includes(q) || hobby.includes(q);
  });
  const hostNameById = Object.fromEntries(
    (users || []).map(u => [String(u?.id || ''), (u?.name || '').trim() || '이름 없음'])
  );
  const reportCountByReported = reports.reduce((acc, r) => {
    const key = `${r.target_user_id || r.reported_id || ''}`;
    if (key && r.status === 'resolved') acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const warningCountByReported = reports.reduce((acc, r) => {
    const key = `${r.target_user_id || r.reported_id || ''}`;
    if (key && String(r?.resolution_note || '').toUpperCase() === 'WARNING') acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const getReportedCount = (r) => {
    const key = `${r.target_user_id || r.reported_id || ''}`;
    return reportCountByReported[key] || 0;
  };
  const getWarningCount = (r) => {
    const key = `${r.target_user_id || r.reported_id || ''}`;
    const byReportStatus = warningCountByReported[key] || 0;
    const userId = String(r?.target_user_id || r?.reported_id || '').trim();
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId);
    if (!isUuid) return byReportStatus;
    return Math.max(byReportStatus, warningCountByUserId[userId] || 0);
  };
  const isKickedFromClub = (r) => {
    return String(r?.resolution_note || '').toUpperCase() === 'KICK';
  };

  // ── 클럽 ───────────────────────────────────
  const handleCreateClub = async () => {
    if (!newClub.times.length) { alert('가능 시간을 하나 이상 선택해주세요.'); return; }
    try {
      const { host_id, ...clubData } = newClub;
      // Edge Function 대신 직접 DB insert → host_id 확실히 반영, 홈 노출 보장
      const { data: club, error } = await supabase.from('clubs').insert({
        ...clubData,
        host_id: host_id || null,
        is_active: true,
        status: 'recruiting',
        members: host_id ? 1 : 0,
      }).select().maybeSingle();
      if (error) throw error;
      // 모임장가 있으면 club_members에도 등록 (인원 카운트 정합성)
      if (host_id && club) {
        await supabase.from('club_members').upsert(
          { club_id: club.id, user_id: host_id },
          { onConflict: 'club_id,user_id', ignoreDuplicates: true }
        );
      }
      setShowCreateClub(false);
      setNewClub({ hobby:'wine', region:'서울', age_group:'all', ratio:'mixed', times:[], max_members:6, host_id:'' });
      await reload('clubs');
      showNotif('모임방이 생성됐어요!');
    } catch (e) {
      logAdminUi('handleCreateClub', e?.message, e);
      showNotif('모임방 생성에 실패했습니다. 입력값·DB 권한을 확인하거나 서버 로그를 확인해주세요.', 'error');
    }
  };

  const handleDeleteClub = async (id) => {
    if (!window.confirm('이 모임방을 삭제할까요?')) return;
    try {
      await adminApi(verifiedPin, 'delete_club', { id });
      setClubs(prev => prev.filter(c => c.id !== id)); // 즉시 로컬 제거
      await reload('clubs'); // 서버 동기화
      showNotif('모임방 삭제 완료', 'error');
    } catch (e) {
      logAdminUi('handleDeleteClub', e?.message, e);
      showNotif('모임방 삭제에 실패했습니다. 잠시 후 다시 시도해주세요.', 'error');
    }
  };

  const handleForceOpen = async (id) => {
    const target = (clubs || []).find(c => c.id === id);
    if (isClubOpenForChat(target)) {
      showNotif('이미 오픈된 방입니다.');
      return;
    }
    if (!window.confirm('인원이 미달이어도 이 방을 강제 오픈할까요?\n채팅방이 즉시 활성화됩니다.')) return;
    try {
      try {
        const res = await adminApi(verifiedPin, 'force_open_club', { id });
        if (res?.success === false) throw new Error(res?.error || '강제 오픈 실패');
      } catch (edgeErr) {
        // Edge Function이 구버전(해당 action 없음)인 환경 폴백
        const { error: fallbackErr } = await supabase
          .from('clubs')
          .update({ status: 'active', is_active: true })
          .eq('id', id);
        if (fallbackErr) throw fallbackErr;
        console.warn('force_open_club Edge 실패, DB 직접 업데이트로 폴백:', edgeErr?.message || edgeErr);
      }
      await reload('clubs');
      showNotif('방이 강제 오픈되었습니다!');
    } catch (e) {
      logAdminUi('handleForceOpen', e?.message, e);
      showNotif('강제 오픈에 실패했습니다. 잠시 후 다시 시도해주세요.', 'error');
    }
  };

  const handleCoinAdjust = async (userId, delta, reason, coinType = 'paid') => {
    const n = parseInt(delta);
    if (!n || n === 0) { alert('수량을 입력해주세요.'); return; }
    try {
      await adminApi(verifiedPin, 'adjust_coins', { user_id: userId, delta: n, reason: reason || '관리자 조정', coin_type: coinType });
      setCoinInputs(prev => ({ ...prev, [userId]: { amount: '', reason: '', coinType } }));
      await reload('coins');
      const typeLabel = coinType === 'free' ? '무료' : '유료';
      showNotif(`${typeLabel} ${n > 0 ? n + '포인트 지급' : Math.abs(n) + '포인트 차감'} 완료`);
    } catch (e) {
      logAdminUi('handleCoinAdjust', e?.message, e);
      showNotif(e?.message || '포인트 조정에 실패했습니다.', 'error');
    }
  };

  // ── 신고 ───────────────────────────────────

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('이 처리완료 신고를 삭제할까요?')) return;
    try {
      const res = await adminApi(verifiedPin, 'delete_report_v2', { report_id: reportId });
      if (res?.success === false) throw new Error(res?.error || 'delete_report_v2 failed');
      await reload('reports');
      showNotif('신고 삭제 완료', 'error');
    } catch (e) {
      logAdminUi('handleDeleteReport', e?.message, e);
      showNotif(e?.message || '신고 삭제에 실패했습니다. 잠시 후 다시 시도해주세요.', 'error');
    }
  };


  const handleBanFromProfanityRisk = async (row, duration) => {
    const uid = String(row?.user_id || '').trim();
    if (!uid) return;
    const d = duration === '7d' ? '7d' : duration === 'permanent' ? 'permanent' : '1d';
    const now = Date.now();
    const expiresAt = d === '1d'
      ? new Date(now + 24 * 60 * 60 * 1000).toISOString()
      : d === '7d'
        ? new Date(now + 7 * 24 * 60 * 60 * 1000).toISOString()
        : null;
    const label = displayUserById(uid);
    if (!window.confirm(`${label} 회원을 ${d === '1d' ? '1일' : d === '7d' ? '7일' : '무기한'} 정지 처리할까요?`)) return;
    try {
      const reason = `[욕설 위험 사용자] 자동 누적 욕설(7일) ${Number(row?.ai_flag_count_7d ?? row?.monthly_profanity_count ?? 0)}회`;
      const banRes = await adminApi(verifiedPin, 'ban_user', { identifier: uid, reason, expires_at: expiresAt });
      if (banRes?.success === false) throw new Error(banRes?.error || 'ban_user failed');
      await reload('reports');
      await reload('profanity_risk');
      await reload('banned');
      await reload('account_suspensions');
      showNotif(`${label} 회원 ${d === '1d' ? '1일' : d === '7d' ? '7일' : '무기한'} 정지 완료`, 'error');
    } catch (e) {
      logAdminUi('handleBanFromProfanityRisk', e?.message, e);
      showNotif(e?.message || '정지 처리에 실패했습니다.', 'error');
    }
  };

  const handleWarnFromProfanityRisk = async (row) => {
    const uid = String(row?.user_id || '').trim();
    if (!uid) return;
    const label = displayUserById(uid);
    if (!window.confirm(`${label} 회원에게 경고를 전송할까요?`)) return;
    try {
      const warningMessage =
        '욕설/성적 발언 누적 감지로 관리자 경고가 발송되었습니다. 채팅 시 상호 존중과 배려를 지켜주세요.';
      const warnRes = await adminApi(verifiedPin, 'warn_user', {
        user_id: uid,
        report: {
          reported_id: uid,
          reported_user_id: uid,
          reported_name: label,
          club_id: row?.room_id || null,
        },
        message: warningMessage,
      });
      if (warnRes?.success === false) throw new Error(warnRes?.error || 'warn_user failed');
      await reload('reports');
      showNotif(`${label} 회원에게 경고를 전송했습니다.`);
    } catch (e) {
      logAdminUi('handleWarnFromProfanityRisk', e?.message, e);
      showNotif(e?.message || '경고 전송에 실패했습니다.', 'error');
    }
  };


  const handleResetProfanityCount = async (row) => {
    const uid = String(row?.user_id || '').trim();
    if (!uid) return;
    const label = displayUserById(uid);
    if (!window.confirm(`${label} 회원의 욕설누적(7일)을 초기화할까요?\n\n(영구누적 욕설·불법 전체 카운트는 그대로 유지됩니다)`)) return;
    try {
      const res = await adminApi(verifiedPin, 'reset_profanity_count', { user_id: uid });
      if (res?.success === false) throw new Error(res?.error || 'reset_profanity_count failed');
      showNotif(`${label} 회원 욕설누적(7일)을 초기화했습니다.`);
      await reload('profanity_risk');
    } catch (e) {
      logAdminUi('handleResetProfanityCount', e?.message, e);
      showNotif(e?.message || '욕설누적(7일) 초기화에 실패했습니다.', 'error');
    }
  };

  const handleResetTotalProfanityCount = async (row) => {
    const uid = String(row?.user_id || '').trim();
    if (!uid) return;
    const label = displayUserById(uid);
    if (!window.confirm(`${label} 회원의 욕설누적(전체)을 0으로 초기화할까요?\n\n(최근 7일 카운트도 0으로 초기화됩니다)`)) return;
    try {
      const res = await adminApi(verifiedPin, 'reset_total_profanity_count', { user_id: uid });
      if (res?.success === false) throw new Error(res?.error || 'reset_total_profanity_count failed');
      showNotif(`${label} 회원 욕설누적(전체)을 초기화했습니다.`);
      await reload('profanity_risk');
    } catch (e) {
      logAdminUi('handleResetTotalProfanityCount', e?.message, e);
      showNotif(e?.message || '욕설누적(전체) 초기화에 실패했습니다.', 'error');
    }
  };

  const handleResetIllegalCount = async (row) => {
    const uid = String(row?.user_id || '').trim();
    if (!uid) return;
    const label = displayUserById(uid);
    if (!window.confirm(`${label} 회원의 불법·조건만남 누적(7일)을 초기화할까요?\n\n(누적 불법·욕설 전체 카운트는 그대로 유지됩니다)`)) return;
    try {
      const res = await adminApi(verifiedPin, 'reset_illegal_count', { user_id: uid });
      if (res?.success === false) throw new Error(res?.error || 'reset_illegal_count failed');
      showNotif(`${label} 회원 불법·조건만남 누적(7일)을 초기화했습니다.`);
      await reload('illegal_users');
    } catch (e) {
      logAdminUi('handleResetIllegalCount', e?.message, e);
      showNotif(e?.message || '불법 누적(7일) 초기화에 실패했습니다.', 'error');
    }
  };

  const handleResetTotalIllegalCount = async (row) => {
    const uid = String(row?.user_id || '').trim();
    if (!uid) return;
    const label = displayUserById(uid);
    if (!window.confirm(`${label} 회원의 불법·조건만남 누적(전체)을 0으로 초기화할까요?\n\n(최근 7일 불법 카운트도 0으로 초기화됩니다)`)) return;
    try {
      const res = await adminApi(verifiedPin, 'reset_total_illegal_count', { user_id: uid });
      if (res?.success === false) throw new Error(res?.error || 'reset_total_illegal_count failed');
      showNotif(`${label} 회원 불법·조건만남 누적(전체)을 초기화했습니다.`);
      await reload('illegal_users');
    } catch (e) {
      logAdminUi('handleResetTotalIllegalCount', e?.message, e);
      showNotif(e?.message || '불법 누적(전체) 초기화에 실패했습니다.', 'error');
    }
  };

  const handleBanFromIllegalRisk = async (row, duration) => {
    const uid = String(row?.user_id || '').trim();
    if (!uid) return;
    const d = duration === '7d' ? '7d' : duration === 'permanent' ? 'permanent' : '1d';
    const now = Date.now();
    const expiresAt = d === '1d'
      ? new Date(now + 24 * 60 * 60 * 1000).toISOString()
      : d === '7d'
        ? new Date(now + 7 * 24 * 60 * 60 * 1000).toISOString()
        : null;
    const label = displayUserById(uid);
    if (!window.confirm(`${label} 회원을 ${d === '1d' ? '1일' : d === '7d' ? '7일' : '무기한'} 정지 처리할까요?`)) return;
    try {
      const reason = `[불법·조건만남 위험] 자동 누적(7일) ${Number(row?.illegal_flag_count_7d ?? 0)}회`;
      const banRes = await adminApi(verifiedPin, 'ban_user', { identifier: uid, reason, expires_at: expiresAt });
      if (banRes?.success === false) throw new Error(banRes?.error || 'ban_user failed');
      await reload('reports');
      await reload('illegal_users');
      await reload('profanity_risk');
      await reload('banned');
      showNotif(`${label} 회원 ${d === '1d' ? '1일' : d === '7d' ? '7일' : '무기한'} 정지 완료`, 'error');
    } catch (e) {
      logAdminUi('handleBanFromIllegalRisk', e?.message, e);
      showNotif(e?.message || '정지 처리에 실패했습니다.', 'error');
    }
  };

  const handleAddIllegalKeyword = async () => {
    const phrase = String(newIllegalPhrase || '').trim();
    if (!phrase) {
      showNotif('키워드를 입력해주세요.', 'error');
      return;
    }
    try {
      await adminApi(verifiedPin, 'add_illegal_keyword', { phrase });
      setNewIllegalPhrase('');
      await reload('illegal_users');
      showNotif('불법 키워드를 추가했습니다.');
    } catch (e) {
      logAdminUi('handleAddIllegalKeyword', e?.message, e);
      showNotif(e?.message || '키워드 추가에 실패했습니다.', 'error');
    }
  };

  const handleDeleteIllegalKeyword = async (id) => {
    if (!window.confirm('이 키워드를 삭제할까요?')) return;
    try {
      await adminApi(verifiedPin, 'delete_illegal_keyword', { id });
      await reload('illegal_users');
      showNotif('삭제했습니다.');
    } catch (e) {
      logAdminUi('handleDeleteIllegalKeyword', e?.message, e);
      showNotif(e?.message || '삭제에 실패했습니다.', 'error');
    }
  };

  const handleToggleIllegalKeyword = async (id, nextActive) => {
    try {
      await adminApi(verifiedPin, 'toggle_illegal_keyword', { id, is_active: nextActive });
      await reload('illegal_users');
      showNotif(nextActive ? '키워드를 활성화했습니다.' : '키워드를 비활성화했습니다.');
    } catch (e) {
      logAdminUi('handleToggleIllegalKeyword', e?.message, e);
      showNotif(e?.message || '상태 변경에 실패했습니다.', 'error');
    }
  };


  const handleWarnFromReportUnified = async (report) => {
    const res = await adminApi(verifiedPin, 'apply_report_action_v2', { report_id: report?.id, action_type: 'warning' });
    if (res?.success === false) throw new Error(res?.error || 'apply_report_action_v2 warning failed');
    await reload('reports');
  };

  const handleBanFromReportUnified = async (report, duration) => {
    const actionType = duration === 'permanent'
      ? 'permanent_ban'
      : duration === '7d'
        ? 'suspend_7d'
        : 'suspend_1d';
    const res = await adminApi(verifiedPin, 'apply_report_action_v2', { report_id: report?.id, action_type: actionType });
    if (res?.success === false) throw new Error(res?.error || 'apply_report_action_v2 suspend failed');
    await reload('reports');
  };

  const handleKickFromReportV2 = async (report) => {
    const res = await adminApi(verifiedPin, 'apply_report_action_v2', { report_id: report?.id, action_type: 'kick' });
    if (res?.success === false) throw new Error(res?.error || 'apply_report_action_v2 kick failed');
    await reload('reports');
  };

  // resetProfanityModal은 레거시(사유 입력 모달) 호환용으로만 남겨둠

  const handleKickOffFromReport = async (report) => {
    try {
      const res = await adminApi(verifiedPin, 'unkick_from_club', { report });
      if (res?.success === false) throw new Error(res?.error || 'unkick_from_club failed');
      if (res?.club_id && res?.user_id) {
        setKickedByClubUser(prevMap => {
          const next = { ...prevMap };
          delete next[`${res.club_id}::${res.user_id}`];
          return next;
        });
      }
      if (report?.id) {
        setKickedReportIds(prevMap => {
          const next = { ...prevMap };
          delete next[String(report.id)];
          return next;
        });
      }
      reload('reports');
      showNotif(`${report.reported_name} 회원의 강제퇴장을 해제했습니다.`);
    } catch (e) {
      logAdminUi('handleKickOffFromReport', e?.message, e);
      showNotif(e?.message || '강퇴 해제에 실패했습니다.', 'error');
    }
  };

  const displayUserById = (uid) => {
    const s = String(uid || '').trim();
    if (!s) return '-';
    const u = users.find((x) => String(x.id) === s);
    if (u) {
      const name = String(u.name || '').trim();
      const nick = String(u.nickname || '').trim();
      if (name && nick && name !== nick) return `${name}(${nick})`;
      return String(nick || name || u.phone || u.email || s).trim() || s;
    }
    return `${s.slice(0, 8)}…`;
  };

  const handleUnkickHostKick = async (row) => {
    const clubId = String(row?.club_id || '').trim();
    const userId = String(row?.user_id || '').trim();
    if (!clubId || !userId) return;
    if (!window.confirm('이 모임장 강퇴 기록을 삭제할까요?\n해당 회원은 이 모임에 다시 참여 신청이 가능해집니다. (자동으로 방에 다시 넣지는 않습니다.)')) return;
    try {
      const res = await adminApi(verifiedPin, 'unkick_host_kick', { club_id: clubId, user_id: userId });
      if (res?.success === false) throw new Error(res?.error || 'unkick_host_kick failed');
      const doneKey = `${clubId}::${userId}`;
      setHostKickDoneByKey((prev) => {
        if (!prev?.[doneKey]) return prev;
        const next = { ...prev };
        delete next[doneKey];
        try { localStorage.setItem(HOST_KICK_DONE_LS_KEY, JSON.stringify(next)); } catch {}
        return next;
      });
      await reload('host_kicks');
      await reload('clubs');
      showNotif('모임장 강퇴 기록을 삭제했습니다.');
    } catch (e) {
      logAdminUi('handleUnkickHostKick', e?.message, e);
      showNotif(e?.message || '처리에 실패했습니다.', 'error');
    }
  };

  // ── 공지사항 ────────────────────────────────
  const handleAddNotice = async () => {
    if (!noticeTitle.trim()) { alert('제목을 입력해주세요.'); return; }
    try {
      await adminApi(verifiedPin, 'create_notice', { title: noticeTitle, content: noticeContent, target: noticeTarget, active: true });
      setNoticeTitle(''); setNoticeContent('');
      await reload('notices');
      showNotif('공지사항이 발송됐어요!');
    } catch (e) {
      logAdminUi('handleAddNotice', e?.message, e);
      showNotif(e?.message || '공지 발송에 실패했습니다.', 'error');
    }
  };

  const handleToggleNotice = async (id) => {
    try {
      await adminApi(verifiedPin, 'toggle_notice', { id });
      await reload('notices');
      showNotif('공지 상태 변경 완료');
    } catch (e) {
      logAdminUi('handleToggleNotice', e?.message, e);
      showNotif(e?.message || '공지 상태 변경에 실패했습니다.', 'error');
    }
  };

  const handleDeleteNotice = async (id) => {
    try {
      await adminApi(verifiedPin, 'delete_notice', { id });
      await reload('notices');
      showNotif('공지사항 삭제 완료', 'error');
    } catch (e) {
      logAdminUi('handleDeleteNotice', e?.message, e);
      showNotif(e?.message || '공지 삭제에 실패했습니다.', 'error');
    }
  };

  const handleReleaseChatBan = async (row) => {
    const cancelReason = window.prompt('채팅 금지 해제 사유를 입력해주세요. (필수)');
    if (!cancelReason || !cancelReason.trim()) {
      showNotif('해제 사유를 입력해야 합니다.', 'error');
      return;
    }
    try {
      await adminApi(verifiedPin, 'release_chat_ban', {
        sanction_id: row?.id || null,
        user_id: row?.user_id || null,
        room_id: row?.room_id || null,
        cancel_reason: cancelReason.trim(),
      });
      await reload('chat_bans');
      showNotif('채팅 금지를 해제했습니다.');
    } catch (e) {
      logAdminUi('handleReleaseChatBan', e?.message, e);
      showNotif(e?.message || '채팅 금지 해제에 실패했습니다.', 'error');
    }
  };

  const handleReleaseRoomKick = async (row) => {
    try {
      await adminApi(verifiedPin, 'release_room_kick', {
        club_id: row?.club_id,
        user_id: row?.user_id,
      });
      await reload('room_kicks_manage');
      showNotif('채팅방 강퇴를 해제했습니다.');
    } catch (e) {
      logAdminUi('handleReleaseRoomKick', e?.message, e);
      showNotif(e?.message || '채팅방 강퇴 해제에 실패했습니다.', 'error');
    }
  };

  const handleReleaseAccountSuspension = async (row) => {
    const cancelReason = window.prompt('계정 정지 해제 사유를 입력해주세요. (필수)');
    if (!cancelReason || !cancelReason.trim()) {
      showNotif('해제 사유를 입력해야 합니다.', 'error');
      return;
    }
    try {
      await adminApi(verifiedPin, 'release_account_suspension', {
        sanction_id: row?.id || null,
        user_id: row?.user_id || null,
        cancel_reason: cancelReason.trim(),
      });
      await reload('account_suspensions');
      await reload('banned');
      showNotif('계정 정지를 해제했습니다.');
    } catch (e) {
      logAdminUi('handleReleaseAccountSuspension', e?.message, e);
      showNotif(e?.message || '계정 정지 해제에 실패했습니다.', 'error');
    }
  };

  const handleReleaseUserSanctionsV2 = async (row, sanctionTypes) => {
    const uid = String(row?.user_id || '').trim();
    if (!uid) return;
    const cancelReason = window.prompt('제재 해제 사유를 입력해주세요. (필수)');
    if (!cancelReason || !cancelReason.trim()) {
      showNotif('해제 사유를 입력해야 합니다.', 'error');
      return;
    }
    try {
      const res = await adminApi(verifiedPin, 'release_user_sanctions_v2', {
        user_id: uid,
        sanction_types: Array.isArray(sanctionTypes) ? sanctionTypes : [],
        cancel_reason: cancelReason.trim(),
      });
      if (res?.success === false) throw new Error(res?.error || 'release_user_sanctions_v2 failed');
      await reload('profanity_risk');
      await reload('illegal_users');
      await reload('reports');
      await reload('banned');
      await reload('sanction_release_logs');
      showNotif('제재를 해제했습니다.');
    } catch (e) {
      logAdminUi('handleReleaseUserSanctionsV2', e?.message, e);
      showNotif(e?.message || '제재 해제에 실패했습니다.', 'error');
    }
  };

  const handleApplyUserSanctionV2 = async (row, sanctionType) => {
    const uid = String(row?.user_id || '').trim();
    if (!uid) return;
    const note = window.prompt('제재 적용 사유를 입력해주세요. (필수)');
    if (!note || !note.trim()) {
      showNotif('사유를 입력해야 합니다.', 'error');
      return;
    }
    try {
      const res = await adminApi(verifiedPin, 'apply_user_sanctions_v2', {
        user_id: uid,
        sanction_type: String(sanctionType || ''),
        note: note.trim(),
      });
      if (res?.success === false) throw new Error(res?.error || 'apply_user_sanctions_v2 failed');
      await reload('profanity_risk');
      await reload('illegal_users');
      await reload('reports');
      await reload('banned');
      await reload('sanction_release_logs');
      showNotif('제재를 적용했습니다.');
    } catch (e) {
      logAdminUi('handleApplyUserSanctionV2', e?.message, e);
      showNotif(e?.message || '제재 적용에 실패했습니다.', 'error');
    }
  };

  const sanctionToggleBtnStyle = (isOn, color = 'blue') => {
    if (!isOn) {
      return {
        padding: '8px 14px',
        background: S.white,
        color: S.gray700,
        border: '1px solid ' + S.gray200,
        borderRadius: 8,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 900,
        fontFamily: 'inherit',
      };
    }
    if (color === 'green') {
      return {
        padding: '8px 14px',
        background: '#ECFDF5',
        color: '#166534',
        border: '1px solid #BBF7D0',
        borderRadius: 8,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 900,
        fontFamily: 'inherit',
      };
    }
    if (color === 'red') {
      return {
        padding: '8px 14px',
        background: '#FEF2F2',
        color: '#B91C1C',
        border: '1px solid #FECACA',
        borderRadius: 8,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 900,
        fontFamily: 'inherit',
      };
    }
    return {
      padding: '8px 14px',
      background: '#EFF6FF',
      color: '#1D4ED8',
      border: '1px solid #BFDBFE',
      borderRadius: 8,
      cursor: 'pointer',
      fontSize: 12,
      fontWeight: 900,
      fontFamily: 'inherit',
    };
  };

  const handleDeleteUser = async (u) => {
    const label = `${u.name || '-'} (${u.email || u.phone || u.id?.slice(0, 8)})`;
    const ok = window.confirm(
      `정말 이 유저를 삭제할까요?\n\n대상: ${label}\n\n유저 인증 계정과 관련 데이터가 삭제되며 복구할 수 없습니다.`
    );
    if (!ok) return;
    try {
      const res = await adminApi(verifiedPin, 'delete_user', { id: u.id });
      if (!res?.success) throw new Error(res?.error || '삭제에 실패했습니다.');
      await reload('all');
      showNotif('유저 삭제 완료', 'error');
    } catch (e) {
      logAdminUi('handleDeleteUser', e?.message, e);
      showNotif(e?.message || '유저 삭제에 실패했습니다.', 'error');
    }
  };

  if (!authed) return <PinScreen pin={pin} setPin={setPin} error={pinError} loading={pinLoading} onSubmit={handlePin}/>;

  const toggleNewClubTime = (val) => setNewClub(prev => ({
    ...prev,
    times: prev.times.includes(val) ? prev.times.filter(t=>t!==val) : [...prev.times, val]
  }));

  return (
    <div style={{ fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif", background:'#F0F2F5', minHeight:'100vh' }}>

      {/* 토스트 알림 */}
      {notification && (
        <div style={{ position:'fixed', top:20, right:20, zIndex:9999,
          background: notification.type==='error'?S.red:notification.type==='warning'?S.yellow:S.green,
          color:'white', padding:'12px 20px', borderRadius:10, fontSize:14, fontWeight:700,
          boxShadow:'0 4px 16px rgba(0,0,0,0.2)' }}>
          {notification.msg}
        </div>
      )}

      {/* 로딩 */}
      {loading && (
        <div style={{ position:'fixed', top:0, left:0, right:0, height:3, background:S.green, zIndex:9998,
          animation:'progress 1s ease infinite', opacity:0.8 }}/>
      )}

      {false && resetProfanityModal.open && (<div />)}

      <div style={{ display:'flex', height:'100vh', overflow:'hidden' }}>

        {/* 사이드바 */}
        <div style={{ width:230, background:S.navy, flexShrink:0, display:'flex', flexDirection:'column', padding:'24px 0', overflow:'auto' }}>
          <div style={{ padding:'0 20px 24px', borderBottom:'1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize:20, fontWeight:900, color:'white', letterSpacing:-1 }}>
              동네<span style={{color:S.green}}>사이</span>
            </div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:3, letterSpacing:1 }}>ADMIN · Supabase 연동</div>
          </div>

          <div style={{ flex:1, padding:'16px 12px' }}>
            {[
              { id:'dashboard',      icon:'📊', label:'대시보드' },
              { id:'clubs',          icon:'🏠', label:'모임방', count: newClubNavCount },
              { id:'users',          icon:'👥', label:'유저', count: newUserNavCount },
              { id:'coins',          icon:'🪙', label:'포인트 관리' },
              { id:'reports',        icon:'🚨', label:'신고 관리', count: reportsNavCount },
              { id:'profanity_risk', icon:'🔥', label:'욕설 유저', count: profanityNavCount },
              { id:'illegal_users',  icon:'⚠️', label:'불법 유저', count: illegalNavCount },
              { id:'banned',         icon:'🚫', label:'정지 유저', count: suspendedNavCount },
              { id:'sanction_release_logs', icon:'🧾', label:'정지/제재 해제 사유', count: sanctionLogsNavCount },
              { id:'notices',        icon:'📢', label:'공지사항', count: stats.activeNotices },
            ].map(item => (
              <button key={item.id} onClick={() => {
                if (
                  [
                    'clubs',
                    'users',
                    'reports',
                    'profanity_risk',
                    'illegal_users',
                    'banned',
                    'sanction_release_logs',
                  ].includes(item.id)
                ) {
                  pendingNavAckRef.current = item.id;
                }
                setTab(item.id);
                reload(item.id === 'dashboard' ? 'all' : item.id);
              }}
                style={{
                  width:'100%', padding:'11px 14px', marginBottom:4,
                  background: tab===item.id ? 'rgba(46,155,100,0.2)' : 'none',
                  border: tab===item.id ? '1px solid rgba(46,155,100,0.4)' : '1px solid transparent',
                  borderRadius:9, cursor:'pointer', fontFamily:'inherit',
                  display:'flex', alignItems:'center', gap:10, textAlign:'left',
                  color: tab===item.id ? S.green : 'rgba(255,255,255,0.65)',
                  fontSize:14, fontWeight: tab===item.id ? 700 : 500,
                }}>
                <span>{item.icon}</span>
                <span style={{flex:1, minWidth:0}}>{item.label}</span>
                {renderNavCountBadge(item.count)}
              </button>
            ))}
          </div>

          <div style={{ padding:'16px 12px', borderTop:'1px solid rgba(255,255,255,0.1)' }}>
            <div style={{fontSize:11,color:'rgba(255,255,255,0.3)',textAlign:'center',marginBottom:8}}>
              🔒 Supabase Edge Function 연동
            </div>
            <button onClick={() => { setAuthed(false); setVerifiedPin(''); }}
              style={{ width:'100%', padding:'10px 14px', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:9, cursor:'pointer', fontFamily:'inherit', color:'#FCA5A5', fontSize:13, fontWeight:600 }}>
              🚪 로그아웃
            </button>
          </div>
        </div>

        {/* 메인 컨텐츠 */}
        <div style={{ flex:1, height:'100vh', overflowY:'auto', overflowX:'hidden' }}>
          <div style={{ padding:'28px 32px' }}>

            {/* ── 대시보드 ── */}
            {tab==='dashboard' && (
              <div>
                <div style={{fontSize:22,fontWeight:800,color:S.gray900,marginBottom:6}}>대시보드</div>
                <div style={{fontSize:13,color:S.gray400,marginBottom:24}}>
                  {new Date().toLocaleDateString('ko-KR',{year:'numeric',month:'long',day:'numeric',weekday:'long'})}
                  <span style={{marginLeft:12,color:S.green,fontWeight:600}}>● Supabase 실시간 연동</span>
                </div>

                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:28}}>
                  {[
                    { label:'전체 유저',    value:stats.totalUsers,       sub:'실제 가입 유저',          color:S.blue,   bg:S.blueLight,   icon:'👥' },
                    { label:'모임방',       value:stats.totalClubs,       sub:`모집중 ${stats.recruitingClubs}개`, color:S.green,  bg:S.greenLight,  icon:'🏠' },
                    { label:'정지 계정',    value:stats.bannedCount,      sub:'차단 적용 중',            color:'#7C3AED', bg:'#EDE9FE',   icon:'🚫' },
                    { label:'미처리 신고',  value:stats.pendingReports,   sub:'처리 필요',               color:S.red,    bg:S.redLight,    icon:'🚨' },
                  ].map((card,i) => (
                    <div key={i} style={{background:S.white,borderRadius:14,padding:'20px',border:'0.5px solid '+S.gray200,boxShadow:'0 1px 4px rgba(0,0,0,0.05)'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                        <div style={{fontSize:12,fontWeight:600,color:S.gray400}}>{card.label}</div>
                        <div style={{background:card.bg,borderRadius:8,padding:'5px 8px',fontSize:16}}>{card.icon}</div>
                      </div>
                      <div style={{fontSize:28,fontWeight:800,color:card.color,marginBottom:4}}>{card.value}</div>
                      <div style={{fontSize:11,color:S.gray400}}>{card.sub}</div>
                    </div>
                  ))}
                </div>

                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
                  <div style={{background:S.white,borderRadius:14,padding:20,border:'0.5px solid '+S.gray200}}>
                    <div style={{fontSize:15,fontWeight:700,color:S.gray900,marginBottom:16}}>🚨 미처리 신고</div>
                    {reports.filter(r=>r.status==='pending').length===0
                      ? <div style={{textAlign:'center',padding:'20px 0',color:S.gray400,fontSize:13}}>미처리 신고 없음 ✅</div>
                      : reports.filter(r=>r.status==='pending').slice(0,4).map(r => (
                        <div key={r.id} style={{padding:'10px 0',borderBottom:'0.5px solid '+S.gray100}}>
                          <div style={{fontSize:13,fontWeight:600,color:S.red}}>
                            {r.reporter_name} → {r.reported_name}
                            <span style={{marginLeft:6,fontSize:11,fontWeight:700,color:'#B91C1C',background:'#FEE2E2',padding:'1px 6px',borderRadius:999}}>
                              {getReportedCount(r)}회
                            </span>
                          </div>
                          <div style={{fontSize:11,color:S.gray400}}>{r.reason}</div>
                        </div>
                      ))
                    }
                  </div>
                  <div style={{background:S.white,borderRadius:14,padding:20,border:'0.5px solid '+S.gray200}}>
                    <div style={{fontSize:15,fontWeight:700,color:S.gray900,marginBottom:16}}>🏠 최근 모임방</div>
                    {clubs.length===0
                      ? <div style={{textAlign:'center',padding:'20px 0',color:S.gray400,fontSize:13}}>생성된 모임방 없음</div>
                      : clubs.slice(0,4).map(c => (
                        <div key={c.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'0.5px solid '+S.gray100}}>
                          <div>
                            <div style={{fontSize:13,fontWeight:600,color:S.gray900}}>{c.title || HOBBY_MAP[c.hobby] || c.hobby}</div>
                            <div style={{fontSize:11,color:S.gray400}}>{c.region} · {c.age_group==='all' ? '전체연령' : c.age_group} · {c.members||0}/{c.max_members||6}명</div>
                          </div>
                          <span style={{background:S.greenLight,color:S.green,padding:'3px 8px',borderRadius:5,fontSize:11,fontWeight:700}}>모집중</span>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            )}

            {/* ── 모임방 ── */}
            {tab==='clubs' && (
              <div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{fontSize:22,fontWeight:800,color:S.gray900}}>🏠 모임방 관리</div>
                      <span style={{
                        fontSize: 14,
                        fontWeight: 800,
                        color: S.gray700,
                        background: S.gray100,
                        padding: '4px 12px',
                        borderRadius: 999,
                        border: `1px solid ${S.gray200}`,
                      }}>
                        {clubs.length.toLocaleString('ko-KR')}
                      </span>
                    </div>
                    <div style={{fontSize:12,color:S.gray400,marginTop:4}}>
                      제목 검색으로 빠르게 찾고, 카드 축소로 더 많은 방을 한 번에 확인할 수 있어요.
                    </div>
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <input
                      value={clubSearch}
                      onChange={e => setClubSearch(e.target.value)}
                      placeholder="방 제목 검색"
                      style={{width:220,padding:'10px 12px',border:'1px solid '+S.gray200,borderRadius:9,fontSize:13,fontFamily:'inherit',outline:'none',background:S.white}}
                    />
                    <button onClick={() => setShowCreateClub(true)}
                      style={{padding:'11px 20px',background:S.green,color:'white',border:'none',borderRadius:10,cursor:'pointer',fontSize:14,fontWeight:700,fontFamily:'inherit'}}>
                      + 모임방 만들기
                    </button>
                  </div>
                </div>

                {/* 생성 모달 */}
                {showCreateClub && (
                  <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 24px'}}>
                    <div style={{background:S.white,borderRadius:16,padding:28,width:460,maxHeight:'85vh',overflow:'auto'}}>
                      <div style={{fontSize:18,fontWeight:800,color:S.gray900,marginBottom:20}}>🏠 새 모임방 만들기</div>
                      <div style={{marginBottom:14}}>
                        <div style={{fontSize:12,fontWeight:600,color:S.gray600,marginBottom:6}}>취미 주제</div>
                        <select value={newClub.hobby} onChange={e => setNewClub(p=>({...p,hobby:e.target.value}))}
                          style={{width:'100%',padding:'10px 14px',border:'1px solid '+S.gray200,borderRadius:8,fontSize:14,fontFamily:'inherit',outline:'none'}}>
                          {HOBBY_OPTIONS.map(h => <option key={h.val} value={h.val}>{h.label}</option>)}
                        </select>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:S.gray600,marginBottom:6}}>지역</div>
                          <select value={newClub.region} onChange={e => setNewClub(p=>({...p,region:e.target.value}))}
                            style={{width:'100%',padding:'10px 14px',border:'1px solid '+S.gray200,borderRadius:8,fontSize:14,fontFamily:'inherit',outline:'none'}}>
                            <option value="수도권 전체">수도권 전체</option>
                            <option value="인천">인천</option>
                            <option value="서울">서울</option>
                            <option value="경기">경기</option>
                          </select>
                        </div>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:S.gray600,marginBottom:6}}>연령 표시</div>
                          <select value={newClub.age_group} onChange={e => setNewClub(p=>({...p,age_group:e.target.value}))}
                            style={{width:'100%',padding:'10px 14px',border:'1px solid '+S.gray200,borderRadius:8,fontSize:14,fontFamily:'inherit',outline:'none'}}>
                            {AGE_OPTIONS.map(a => <option key={a} value={a}>{a === 'all' ? '전체연령' : a}</option>)}
                          </select>
                        </div>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:S.gray600,marginBottom:6}}>모임 타입</div>
                          <select value={newClub.ratio} onChange={e => setNewClub(p=>({...p,ratio:e.target.value}))}
                            style={{width:'100%',padding:'10px 14px',border:'1px solid '+S.gray200,borderRadius:8,fontSize:14,fontFamily:'inherit',outline:'none'}}>
                            <option value="mixed">혼합 모임</option>
                            <option value="female">여성전용</option>
                            <option value="male">남성전용</option>
                          </select>
                        </div>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:S.gray600,marginBottom:6}}>최대 인원</div>
                          <select value={newClub.max_members} onChange={e => setNewClub(p=>({...p,max_members:parseInt(e.target.value)}))}
                            style={{width:'100%',padding:'10px 14px',border:'1px solid '+S.gray200,borderRadius:8,fontSize:14,fontFamily:'inherit',outline:'none'}}>
                            <option value={5}>5명</option><option value={6}>6명</option>
                          </select>
                        </div>
                      </div>
                      <div style={{marginBottom:14}}>
                        <div style={{fontSize:12,fontWeight:600,color:S.gray600,marginBottom:6}}>모임장 지정 <span style={{color:S.red,fontSize:11}}>* 홈 노출에 필수</span></div>
                        <select value={newClub.host_id} onChange={e => setNewClub(p=>({...p,host_id:e.target.value}))}
                          style={{width:'100%',padding:'10px 14px',border:'1px solid '+S.gray200,borderRadius:8,fontSize:14,fontFamily:'inherit',outline:'none'}}>
                          <option value="">선택 안 함 (홈에 미노출)</option>
                          {users.map(u => (
                            <option key={u.id} value={u.id}>
                              {u.name}{u.man_age != null ? ` · 만${u.man_age}세` : ''} ({u.phone||u.email||u.id?.slice(0,8)})
                            </option>
                          ))}
                        </select>
                        <div style={{fontSize:11,color:S.gray400,marginTop:3}}>모임장으로 지정된 사용자가 방 생성자가 됩니다</div>
                      </div>
                      <div style={{marginBottom:20}}>
                        <div style={{fontSize:12,fontWeight:600,color:S.gray600,marginBottom:8}}>가능 시간 (복수 선택)</div>
                        <div style={{display:'flex',gap:8}}>
                          {TIME_OPTIONS.map(t => (
                            <button key={t.val} onClick={() => toggleNewClubTime(t.val)}
                              style={{flex:1,padding:'9px',
                                background:newClub.times.includes(t.val)?S.greenLight:S.gray100,
                                color:newClub.times.includes(t.val)?S.green:S.gray600,
                                border:newClub.times.includes(t.val)?'1.5px solid '+S.green:'1.5px solid '+S.gray200,
                                borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:700,fontFamily:'inherit'}}>
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div style={{display:'flex',gap:10}}>
                        <button onClick={() => setShowCreateClub(false)}
                          style={{flex:1,padding:13,background:S.gray100,border:'none',borderRadius:10,cursor:'pointer',fontSize:14,fontWeight:600,fontFamily:'inherit',color:S.gray600}}>취소</button>
                        <button onClick={handleCreateClub}
                          style={{flex:1,padding:13,background:S.green,border:'none',borderRadius:10,cursor:'pointer',fontSize:14,fontWeight:700,fontFamily:'inherit',color:'white'}}>생성하기</button>
                      </div>
                    </div>
                  </div>
                )}

                {clubs.length===0
                  ? <div style={{background:S.white,borderRadius:14,padding:'48px',textAlign:'center',color:S.gray400,border:'0.5px solid '+S.gray200}}>
                      아직 모임방이 없어요.<br/>위의 버튼으로 첫 모임방을 만들어주세요!
                    </div>
                  : filteredClubs.length===0
                    ? <div style={{background:S.white,borderRadius:14,padding:'42px',textAlign:'center',color:S.gray400,border:'0.5px solid '+S.gray200}}>
                        검색 결과가 없어요.
                      </div>
                    : <div style={{display:'grid',gridTemplateColumns:'repeat(3,minmax(0,1fr))',gap:10}}>
                      {filteredClubs.map(c => {
                        const isOpen = isClubOpenForChat(c);
                        return (
                        <div key={c.id} style={{background:S.white,borderRadius:12,padding:12,border:'0.5px solid '+S.gray200}}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8,gap:8}}>
                            <div>
                              <div style={{fontSize:14,fontWeight:700,color:S.gray900,lineHeight:1.35}}>{c.title || HOBBY_MAP[c.hobby] || c.hobby}</div>
                              {c.title && <div style={{fontSize:11,color:S.gray400,marginTop:1}}>{HOBBY_MAP[c.hobby]||c.hobby}</div>}
                            </div>
                            <span style={{
                              background: isOpen ? S.blueLight : (c.members||0)>=(c.max_members||6) ? S.redLight : S.greenLight,
                              color: isOpen ? S.blue : (c.members||0)>=(c.max_members||6) ? S.red : S.green,
                              fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:5,whiteSpace:'nowrap'}}>
                              {isOpen ? '⚡오픈' : (c.members||0)>=(c.max_members||6) ? '마감' : '모집중'}
                            </span>
                          </div>
                          <div style={{display:'flex',gap:5,flexWrap:'wrap',marginBottom:8}}>
                            {[
                              c.region,
                              c.host_id ? `모임장 ${hostNameById[String(c.host_id)] || '알 수 없음'}` : '모임장 미지정',
                              (c.room_type === 'age_limited' && c.min_age && c.max_age) ? `${c.min_age}~${c.max_age}세` : ((c.age_group === 'all') ? '전체연령' : (c.age_group || '')),
                              c.ratio==='female'?'여성전용':c.ratio==='male'?'남성전용':'혼합',
                              ...(c.times||[]).map(t=>TIME_OPTIONS.find(o=>o.val===t)?.label||t)
                            ].filter(Boolean).map((tag,i) => (
                              <span key={i} style={{background:S.gray100,color:S.gray600,fontSize:10,padding:'2px 6px',borderRadius:5}}>{tag}</span>
                            ))}
                          </div>
                          <div style={{marginBottom:8}}>
                            <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:S.gray400,marginBottom:4}}>
                              <span>참여 인원</span>
                              <span style={{fontWeight:700,color:S.green}}>{c.members||0}/{c.max_members||6}명</span>
                            </div>
                            <div style={{height:5,background:S.gray100,borderRadius:3,overflow:'hidden'}}>
                              <div style={{height:'100%',background:S.green,borderRadius:3,width:`${Math.min(100,((c.members||0)/(c.max_members||6))*100)}%`}}/>
                            </div>
                          </div>
                          <div style={{fontSize:10,color:S.gray400,marginBottom:8}}>생성일: {c.created_at?.slice(0,10)}</div>
                          {!isOpen && (
                            <button onClick={() => handleForceOpen(c.id)}
                              style={{width:'100%',padding:'7px',marginBottom:5,background:S.yellowLight,color:S.yellow,border:'none',borderRadius:8,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>
                              ⚡ 강제 오픈
                            </button>
                          )}
                          <button onClick={() => handleDeleteClub(c.id)}
                            style={{width:'100%',padding:'7px',background:S.redLight,color:S.red,border:'none',borderRadius:8,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>
                            🗑 모임방 삭제
                          </button>
                        </div>
                      )})}
                    </div>
                }
              </div>
            )}

            {/* ── 유저 ── */}
            {tab==='users' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
                  <div style={{fontSize:22,fontWeight:800,color:S.gray900}}>👥 실제 가입 유저</div>
                  <span style={{
                    fontSize: 14,
                    fontWeight: 800,
                    color: S.gray700,
                    background: S.gray100,
                    padding: '4px 12px',
                    borderRadius: 999,
                    border: `1px solid ${S.gray200}`,
                  }}>
                    {users.length.toLocaleString('ko-KR')}
                  </span>
                </div>
                {users.length===0
                  ? <div style={{background:S.white,borderRadius:14,padding:'40px',textAlign:'center',color:S.gray400,border:'0.5px solid '+S.gray200}}>가입된 유저가 없어요</div>
                  : <div style={{background:S.white,borderRadius:14,border:'0.5px solid '+S.gray200,overflow:'hidden'}}>
                      <table style={{width:'100%',borderCollapse:'collapse'}}>
                        <thead>
                          <tr style={{background:S.gray50,borderBottom:'1px solid '+S.gray200}}>
                            {['이름','닉네임','성별','나이','이메일','전화번호','가입일','마지막 로그인','삭제'].map(h => (
                              <th key={h} style={{padding:'12px 16px',textAlign:'left',fontSize:12,fontWeight:700,color:S.gray400}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {users.map(u => {
                            return (
                              <tr key={u.id} style={{borderBottom:'0.5px solid '+S.gray100,background:S.white}}>
                                <td style={{padding:'13px 16px',fontSize:14,fontWeight:600,color:S.gray900}}>{u.name}</td>
                                <td style={{padding:'13px 16px',fontSize:13,color:S.gray700}}>{u.nickname || '-'}</td>
                                <td style={{padding:'13px 16px',fontSize:13,color:S.gray600,whiteSpace:'nowrap'}}>
                                  {u.gender === '남' ? '남성' : u.gender === '여' ? '여성' : (u.gender || '-')}
                                </td>
                                <td style={{padding:'13px 16px',fontSize:13,color:S.gray600,whiteSpace:'nowrap'}}>
                                  {u.year_age != null
                                    ? `${u.year_age}세(연도)`
                                    : u.man_age != null
                                      ? `만 ${u.man_age}세`
                                      : '-'}
                                </td>
                                <td style={{padding:'13px 16px',fontSize:12,color:S.gray600}}>{u.email || '-'}</td>
                                <td style={{padding:'13px 16px',fontSize:12,color:S.gray600}}>{u.phone || '-'}</td>
                                <td style={{padding:'13px 16px',fontSize:12,color:S.gray400}}>{u.created_at?.slice(0,10)}</td>
                                <td style={{padding:'13px 16px',fontSize:12,color:S.gray400}}>{u.last_sign_in?.slice(0,10)||'-'}</td>
                                <td style={{padding:'13px 16px'}}>
                                  <button onClick={() => handleDeleteUser(u)}
                                    style={{padding:'5px 12px',background:S.red,color:'white',border:'none',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>
                                    삭제
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                }
              </div>
            )}

            {/* ── 신고 ── */}
            {tab==='reports' && (() => {
              // 방별 신고 건수 집계
              const roomReportCount = {};
              reports.forEach(r => {
                const key = r.chat_room || r.club_id || '기타';
                roomReportCount[key] = (roomReportCount[key] || 0) + 1;
              });
              return (
              <div>
                <div style={{fontSize:22,fontWeight:800,color:S.gray900,marginBottom:8}}>🚨 신고 관리</div>
                {sanctionReviews.filter((sr) => sr.status === 'pending').length > 0 && (
                  <div style={{ background: S.blueLight, border: '1px solid ' + S.blue, borderRadius: 12, padding: '12px 16px', marginBottom: 14, fontSize: 13, color: S.gray900 }}>
                    <strong style={{ color: S.blue }}>관리자 검토 큐</strong> — 처리 대기{' '}
                    <strong>{sanctionReviews.filter((sr) => sr.status === 'pending').length}</strong>건
                    <span style={{ color: S.gray600, marginLeft: 8 }}>(자동 정지 아님 · 권장 조치만 표시)</span>
                  </div>
                )}
                {/* 방별 신고 현황 요약 */}
                {Object.keys(roomReportCount).length > 0 && (
                  <div style={{background:S.white,borderRadius:12,padding:'14px 18px',marginBottom:16,border:'0.5px solid '+S.gray200}}>
                    <div style={{fontSize:13,fontWeight:700,color:S.gray900,marginBottom:10}}>방별 신고 현황</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                      {Object.entries(roomReportCount).map(([room, cnt]) => (
                        <div key={room} style={{background:cnt>=3?S.redLight:S.yellowLight,color:cnt>=3?S.red:S.yellow,padding:'4px 10px',borderRadius:6,fontSize:12,fontWeight:700}}>
                          {room}: {cnt}건{cnt>=3?' ⚠️ 관리자 검토 필요':''}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {reports.length===0
                  ? <div style={{background:S.white,borderRadius:14,padding:'40px',textAlign:'center',color:S.gray400,border:'0.5px solid '+S.gray200}}>접수된 신고가 없어요 ✅</div>
                  : <div style={{display:'flex',flexDirection:'column',gap:10}}>
                      {reports.map(r => (
                        <div key={r.id} style={{background:r.status==='pending'?'#FFFBEB':S.white,borderRadius:12,padding:'16px 18px',border:'0.5px solid '+(r.status==='pending'?'#FCD34D':S.gray200)}}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                            <div>
                              <div style={{fontSize:14,fontWeight:700,color:S.gray900}}>
                                <span style={{color:S.gray600}}>{r.reporter_name || renderUserNameOnly(r?.reporter_user_id, r?.reporter_profile)}</span>
                                <span style={{color:S.gray400,margin:'0 6px'}}>→</span>
                                <span style={{color:S.red}}>{r.reported_name || renderUserNameOnly(r?.target_user_id, r?.target_profile)}</span>
                                {getWarningCount(r) > 0 ? (
                                  <span style={{marginLeft:4,fontSize:11,fontWeight:700,color:'#92400E',background:'#FEF3C7',padding:'1px 6px',borderRadius:999}}>
                                    경고 {getWarningCount(r)}회 처리
                                  </span>
                                ) : (
                                  <span style={{marginLeft:4,fontSize:11,fontWeight:700,color:'#14532D',background:'#DCFCE7',padding:'1px 6px',borderRadius:999}}>
                                    신고 {getReportedCount(r)}회 처리
                                  </span>
                                )}
                                {isKickedFromClub(r) && (
                                  <span style={{marginLeft:4,fontSize:11,fontWeight:700,color:'#991B1B',background:'#FEE2E2',padding:'1px 6px',borderRadius:999}}>
                                    채팅방 강제퇴장
                                  </span>
                                )}
                              </div>
                              <div style={{fontSize:12,color:S.gray400,marginTop:3}}>
                                방: <strong style={{color:S.gray700}}>{r.chat_room || r?.club_info?.title || r.club_id || '-'}</strong> · {r.created_at?.slice(0,10)}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, background: S.yellowLight, color: S.yellow, padding: '2px 8px', borderRadius: 6 }}>최근 30일 신고 {Number(r.report_count_30d ?? 0)}건</span>
                                <span style={{ fontSize: 11, fontWeight: 700, background: S.gray100, color: S.gray600, padding: '2px 8px', borderRadius: 6 }}>최근 7일 욕설 {Number(r.profanity_count_7d ?? 0)}회</span>
                                <span style={{ fontSize: 11, fontWeight: 700, background: '#FFEDD5', color: '#9A3412', padding: '2px 8px', borderRadius: 6 }}>불법·조건만남(7일) {Number(r.illegal_chat_count_7d ?? 0)}회</span>
                                <span style={{ fontSize: 11, fontWeight: 700, background: '#EDE9FE', color: '#5B21B6', padding: '2px 8px', borderRadius: 6 }}>누적 신고 {Number(r.total_report_count ?? 0)}건</span>
                                <span style={{ fontSize: 11, fontWeight: 700, background: '#DBEAFE', color: '#1D4ED8', padding: '2px 8px', borderRadius: 6 }}>누적 욕설 {Number(r.total_profanity_count ?? 0)}회</span>
                                <span style={{ fontSize: 11, fontWeight: 700, background: '#FED7AA', color: '#C2410C', padding: '2px 8px', borderRadius: 6 }}>누적 불법(전체) {Number(r.total_illegal_chat_count ?? 0)}회</span>
                                {Number(r.profanity_count_7d ?? 0) >= 25 && (
                                  <span style={{ fontSize: 11, fontWeight: 800, background: '#FEE2E2', color: '#B91C1C', padding: '2px 8px', borderRadius: 6 }}>욕설 위험</span>
                                )}
                                {(r.current_room_sanction || r.metric_current_room_sanction) && (
                                  <span style={{ fontSize: 11, fontWeight: 700, background: '#FEE2E2', color: '#991B1B', padding: '2px 8px', borderRadius: 6 }}>방 제재 {String(r.current_room_sanction || r.metric_current_room_sanction)}</span>
                                )}
                                {(r.current_account_sanction || r.metric_current_account_sanction) && (
                                  <span style={{ fontSize: 11, fontWeight: 700, background: '#EDE9FE', color: '#5B21B6', padding: '2px 8px', borderRadius: 6 }}>계정 제재 {String(r.current_account_sanction || r.metric_current_account_sanction)}</span>
                                )}
                              </div>
                            </div>
                            <span style={{background:r.status==='pending'?S.yellowLight:S.greenLight,color:r.status==='pending'?S.yellow:S.green,padding:'3px 9px',borderRadius:5,fontSize:12,fontWeight:700,flexShrink:0}}>
                              {r.status==='pending'?'미처리':'처리완료'}
                            </span>
                          </div>
                          <div style={{fontSize:13,color:S.gray700,marginBottom:r.screenshot_url?10:0,lineHeight:1.6}}>📝 {r.reason}</div>
                          {r.screenshot_url && (
                            <div style={{marginBottom:10}}>
                              <div style={{fontSize:11,color:S.gray400,marginBottom:4}}>📎 첨부 스크린샷</div>
                              <img
                                src={r.screenshot_url}
                                alt="신고 스크린샷"
                                style={{maxWidth:220,maxHeight:160,objectFit:'cover',borderRadius:8,cursor:'pointer',border:'1px solid '+S.gray200}}
                                onClick={() => window.open(r.screenshot_url,'_blank')}
                              />
                            </div>
                          )}
                          {r.status==='pending' && (
                            <div style={{display:'flex',gap:6,marginTop:6}}>
                              <button
                                type="button"
                                onClick={() => handleWarnFromReportUnified(r)}
                                style={{padding:'6px 12px',background:'#FEF3C7',color:'#92400E',border:'1px solid #FDE68A',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}
                              >
                                ⚠️ 경고
                              </button>
                              <button
                                type="button"
                                onClick={() => handleKickFromReportV2(r)}
                                style={{padding:'6px 12px',background:'#FEE2E2',color:'#B91C1C',border:'1px solid #FECACA',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}
                              >
                                ⛔ 강퇴
                              </button>
                              <button
                                type="button"
                                onClick={() => handleBanFromReportUnified(r, '1d')}
                                style={{padding:'6px 12px',background:'#FEF3C7',color:'#92400E',border:'1px solid #FDE68A',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}
                              >
                                1일 정지
                              </button>
                              <button
                                type="button"
                                onClick={() => handleBanFromReportUnified(r, '7d')}
                                style={{padding:'6px 12px',background:'#FEE2E2',color:'#B91C1C',border:'1px solid #FECACA',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}
                              >
                                7일 정지
                              </button>
                              <button
                                type="button"
                                onClick={() => handleBanFromReportUnified(r, 'permanent')}
                                style={{padding:'6px 12px',background:'#EDE9FE',color:'#5B21B6',border:'1px solid #DDD6FE',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}
                              >
                                영구정지
                              </button>
                            </div>
                          )}
                          {false && isKickedFromClub(r) && (
                            <div style={{display:'flex',gap:6,marginTop:6}}>
                              <button onClick={() => handleKickOffFromReport(r)}
                                style={{padding:'6px 12px',background:'#E5E7EB',color:'#374151',border:'none',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>⏻ 강퇴 OFF</button>
                            </div>
                          )}
                          {r.status !== 'pending' && (
                            <div style={{display:'flex',gap:6,marginTop:6}}>
                              <button
                                onClick={() => handleDeleteReport(r.id)}
                                style={{padding:'6px 12px',background:S.redLight,color:S.red,border:'none',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}
                              >
                                🗑 삭제
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                }
              </div>
              );
            })()}

            {false && tab === 'host_kicks' && (
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: S.gray900, marginBottom: 8 }}>⛔ 모임장 강퇴</div>
                <div style={{ fontSize: 13, color: S.gray400, marginBottom: 20, lineHeight: 1.6 }}>
                  모임장이 제출한 사유·캡처가 기록됩니다. 재참여를 허용하려면 아래에서 기록을 삭제하세요.
                </div>
                {hostClubKicks.filter((k) => {
                  const key = `${String(k?.club_id || '').trim()}::${String(k?.user_id || '').trim()}`;
                  return !hostKickDoneByKey?.[key];
                }).length === 0 ? (
                  <div style={{ background: S.white, borderRadius: 14, padding: '40px', textAlign: 'center', color: S.gray400, border: '0.5px solid ' + S.gray200 }}>
                    모임장 강퇴 기록이 없어요
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {hostClubKicks
                      .filter((k) => {
                        const key = `${String(k?.club_id || '').trim()}::${String(k?.user_id || '').trim()}`;
                        return !hostKickDoneByKey?.[key];
                      })
                      .map((k) => {
                      const clubRow = clubs.find((c) => String(c.id) === String(k.club_id));
                      const clubTitle =
                        clubRow?.title || HOBBY_MAP[clubRow?.hobby] || clubRow?.hobby || k.club_id;
                      const key = `${String(k?.club_id || '').trim()}::${String(k?.user_id || '').trim()}`;
                      const hostKickCountInRoom = (hostClubKicks || []).filter((x) =>
                        String(x?.kick_source || '').trim() === 'host'
                        && String(x?.club_id || '').trim() === String(k?.club_id || '').trim()
                        && String(x?.kicked_by_user_id || '').trim() === String(k?.kicked_by_user_id || '').trim()
                      ).length;
                      const risk = hostKickRiskByKey?.[key] || {};
                      const globalRisk = risk?.global_risk || {};
                      return (
                        <div
                          key={`${k.club_id}_${k.user_id}_${k.created_at || ''}`}
                          style={{
                            background: S.white,
                            borderRadius: 12,
                            padding: '16px 18px',
                            border: '0.5px solid ' + S.gray200,
                          }}
                        >
                          <div style={{ fontSize: 12, color: S.gray400, marginBottom: 6 }}>
                            {k.created_at?.slice(0, 19)?.replace('T', ' ') || '-'}
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: S.gray900, marginBottom: 8 }}>
                            모임: <span style={{ color: S.gray700 }}>{clubTitle}</span>
                          </div>
                          <div style={{ fontSize: 13, color: S.gray600, marginBottom: 4 }}>
                            강퇴 대상: <strong style={{ color: S.gray900 }}>{displayUserById(k.user_id)}</strong>{' '}
                            <span style={{ color: S.gray400, fontSize: 11 }}>({String(k.user_id || '').slice(0, 8)}…)</span>
                          </div>
                          <div style={{ fontSize: 13, color: S.gray600, marginBottom: 10 }}>
                            처리 모임장: <strong style={{ color: S.gray900 }}>{displayUserById(k.kicked_by_user_id)}</strong>
                          </div>
                          <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:10}}>
                            <span style={{fontSize:11,fontWeight:700,background:'#EEF2FF',color:'#3730A3',padding:'2px 8px',borderRadius:6}}>
                              이 방에서 해당 모임장 총 강퇴 {hostKickCountInRoom}회
                            </span>
                            <span style={{fontSize:11,fontWeight:700,background:'#FEF3C7',color:'#92400E',padding:'2px 8px',borderRadius:6}}>
                              대상자 전체 욕설(7일) {Number(globalRisk?.ai_flag_count_7d ?? 0)}회
                            </span>
                            <span style={{fontSize:11,fontWeight:700,background:'#FEF3C7',color:'#92400E',padding:'2px 8px',borderRadius:6}}>
                              대상자 전체 신고(30일) {Number(globalRisk?.global_report_count ?? 0)}건
                            </span>
                          </div>
                          <div style={{ fontSize: 13, color: S.gray700, lineHeight: 1.65, whiteSpace: 'pre-wrap', marginBottom: 10 }}>
                            <span style={{ fontWeight: 700, color: S.gray900 }}>사유</span>
                            <br />
                            {k.kick_detail || '(내용 없음)'}
                          </div>
                          {k.screenshot_url ? (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: S.gray400, marginBottom: 4 }}>제출 캡처</div>
                              <img
                                src={k.screenshot_url}
                                alt="모임장 강퇴 캡처"
                                style={{
                                  maxWidth: 280,
                                  maxHeight: 200,
                                  objectFit: 'cover',
                                  borderRadius: 8,
                                  cursor: 'pointer',
                                  border: '1px solid ' + S.gray200,
                                }}
                                onClick={() => window.open(k.screenshot_url, '_blank')}
                              />
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: S.red, marginBottom: 12 }}>캡처 URL 없음 (구버전 데이터일 수 있음)</div>
                          )}
                          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                            <button
                              type="button"
                              onClick={() => handleUnkickHostKick(k)}
                              style={{
                                padding: '8px 14px',
                                background: S.gray100,
                                color: S.gray900,
                                border: '1px solid ' + S.gray200,
                                borderRadius: 8,
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 700,
                                fontFamily: 'inherit',
                              }}
                            >
                              재참여 허용 (강퇴 기록 삭제)
                            </button>
                            <button
                              type="button"
                              onClick={() => markHostKickDone(k)}
                              style={{
                                padding: '8px 14px',
                                background: '#DCFCE7',
                                color: '#166534',
                                border: '1px solid #BBF7D0',
                                borderRadius: 8,
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 700,
                                fontFamily: 'inherit',
                              }}
                            >
                              처리완료
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === 'profanity_risk' && (
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: S.gray900, marginBottom: 8 }}>🔥 욕설 위험 사용자</div>
                <div style={{ fontSize: 13, color: S.gray400, marginBottom: 20, lineHeight: 1.6 }}>
                  신고가 없어도 욕설 누적이 높으면 자동으로 이 목록에 표시됩니다. (15회 검토, 20회 1일 정지 추천, 25회 7일 정지 추천)
                </div>

                {profanityRiskUsers.length === 0 ? (
                  <div style={{ background: S.white, borderRadius: 14, padding: '40px', textAlign: 'center', color: S.gray400, border: '0.5px solid ' + S.gray200 }}>
                    현재 욕설 위험 사용자가 없어요
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {profanityRiskUsers.map((u) => {
                      const rec = String(u?.recommendation || '');
                      const isHandled = !Array.isArray(u?.pending_reviews) || u.pending_reviews.length === 0;
                      const recText = rec === 'ACCOUNT_SUSPEND_7D_RECOMMEND'
                        ? '7일 정지 추천'
                        : rec === 'PERMANENT_BAN_REVIEW'
                          ? '영구정지 추천'
                        : rec === 'ACCOUNT_SUSPEND_1D_RECOMMEND'
                          ? '1일 정지 추천'
                          : rec === 'CHAT_BAN_24H_REVIEW'
                            ? '24시간 채팅 제한'
                            : rec === 'CHAT_BAN_10M_REVIEW'
                              ? '10분 채팅 제한'
                              : '욕설 유저';
                      return (
                        <div key={String(u?.user_id || '')} style={{ background: S.white, borderRadius: 12, padding: '16px 18px', border: '0.5px solid ' + S.gray200 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: S.gray900 }}>
                              {String(u?.display_name || '').trim() || displayUserById(u?.user_id)}
                              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, color: '#B91C1C', background: '#FEE2E2', padding: '2px 8px', borderRadius: 6 }}>
                                {recText}
                              </span>
                              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 800, color: isHandled ? '#6B7280' : '#92400E', background: isHandled ? '#FFFFFF' : '#FEF3C7', border: isHandled ? '1px solid #E5E7EB' : '1px solid #FDE68A', padding: '2px 8px', borderRadius: 6 }}>
                                {isHandled ? '처리완료' : '미처리'}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: S.gray400 }}>{String(u?.user_id || '').slice(0, 8)}…</div>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, background: S.gray100, color: S.gray700, padding: '2px 8px', borderRadius: 6 }}>전체 신고(30일) {Number(u?.report_count_30d ?? u?.global_report_count ?? 0)}건</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: S.gray100, color: S.gray700, padding: '2px 8px', borderRadius: 6 }}>욕설 누적(7일) {Number(u?.ai_flag_count_7d ?? u?.monthly_profanity_count ?? 0)}회</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: '#FFEDD5', color: '#9A3412', padding: '2px 8px', borderRadius: 6 }}>불법·조건만남(7일) {Number(u?.illegal_flag_count_7d ?? 0)}회</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: '#EEF2FF', color: '#3730A3', padding: '2px 8px', borderRadius: 6 }}>영구누적 신고(전체) {Number(u?.total_report_count ?? 0)}건</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: '#F5F3FF', color: '#6D28D9', padding: '2px 8px', borderRadius: 6 }}>영구누적 욕설(전체) {Number(u?.total_ai_flag_count ?? 0)}회</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: '#FED7AA', color: '#C2410C', padding: '2px 8px', borderRadius: 6 }}>누적 불법(전체) {Number(u?.total_illegal_flag_count ?? 0)}회</span>
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {!isHandled && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleWarnFromProfanityRisk(u)}
                                  style={{ padding: '8px 14px', background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}
                                >
                                  ⚠️ 경고
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleBanFromProfanityRisk(u, '1d')}
                                  style={{ padding: '8px 14px', background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}
                                >
                                  1일 정지
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleBanFromProfanityRisk(u, '7d')}
                                  style={{ padding: '8px 14px', background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FECACA', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}
                                >
                                  7일 정지
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleBanFromProfanityRisk(u, 'permanent')}
                                  style={{ padding: '8px 14px', background: '#EDE9FE', color: '#5B21B6', border: '1px solid #DDD6FE', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}
                                >
                                  영구정지
                                </button>
                              </>
                            )}
                            {/* ON/OFF 토글(해제): 현재 제재가 걸려있으면 ON, 누르면 OFF(해제) */}
                            <button
                              type="button"
                              onClick={() => u?.active_sanctions?.CHAT_BAN_10M ? handleReleaseUserSanctionsV2(u, ['CHAT_BAN_10M']) : handleApplyUserSanctionV2(u, 'CHAT_BAN_10M')}
                              style={sanctionToggleBtnStyle(Boolean(u?.active_sanctions?.CHAT_BAN_10M), 'blue')}
                            >
                              10분 챗밴 {u?.active_sanctions?.CHAT_BAN_10M ? 'ON' : 'OFF'}
                            </button>
                            <button
                              type="button"
                              onClick={() => u?.active_sanctions?.CHAT_BAN_24H ? handleReleaseUserSanctionsV2(u, ['CHAT_BAN_24H']) : handleApplyUserSanctionV2(u, 'CHAT_BAN_24H')}
                              style={sanctionToggleBtnStyle(Boolean(u?.active_sanctions?.CHAT_BAN_24H), 'blue')}
                            >
                              24시간 챗밴 {u?.active_sanctions?.CHAT_BAN_24H ? 'ON' : 'OFF'}
                            </button>
                            <button
                              type="button"
                              onClick={() => u?.active_sanctions?.ACCOUNT_SUSPEND_1D ? handleReleaseUserSanctionsV2(u, ['ACCOUNT_SUSPEND_1D']) : handleApplyUserSanctionV2(u, 'ACCOUNT_SUSPEND_1D')}
                              style={sanctionToggleBtnStyle(Boolean(u?.active_sanctions?.ACCOUNT_SUSPEND_1D), 'green')}
                            >
                              1일 정지 {u?.active_sanctions?.ACCOUNT_SUSPEND_1D ? 'ON' : 'OFF'}
                            </button>
                            <button
                              type="button"
                              onClick={() => u?.active_sanctions?.ACCOUNT_SUSPEND_7D ? handleReleaseUserSanctionsV2(u, ['ACCOUNT_SUSPEND_7D']) : handleApplyUserSanctionV2(u, 'ACCOUNT_SUSPEND_7D')}
                              style={sanctionToggleBtnStyle(Boolean(u?.active_sanctions?.ACCOUNT_SUSPEND_7D), 'green')}
                            >
                              7일 정지 {u?.active_sanctions?.ACCOUNT_SUSPEND_7D ? 'ON' : 'OFF'}
                            </button>
                            <button
                              type="button"
                              onClick={() => u?.active_sanctions?.PERMANENT_BAN ? handleReleaseUserSanctionsV2(u, ['PERMANENT_BAN']) : handleApplyUserSanctionV2(u, 'PERMANENT_BAN')}
                              style={sanctionToggleBtnStyle(Boolean(u?.active_sanctions?.PERMANENT_BAN), 'red')}
                            >
                              영구정지 {u?.active_sanctions?.PERMANENT_BAN ? 'ON' : 'OFF'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleResetProfanityCount(u)}
                              style={{ padding: '8px 14px', background: S.gray100, color: S.gray700, border: '1px solid ' + S.gray200, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}
                            >
                              욕설누적(7일) 초기화
                            </button>
                            <button
                              type="button"
                              onClick={() => handleResetTotalProfanityCount(u)}
                              style={{ padding: '8px 14px', background: '#F3F4F6', color: S.gray900, border: '1px solid ' + S.gray200, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 900, fontFamily: 'inherit' }}
                            >
                              욕설누적(전체) 초기화
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === 'illegal_users' && (
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: S.gray900, marginBottom: 8 }}>불법 유저</div>
                <div style={{ fontSize: 13, color: S.gray400, marginBottom: 18, lineHeight: 1.6 }}>
                  관리자가 등록한 <strong>불법 키워드</strong> 또는 <strong>조건만남</strong> 로컬 필터에 걸린 채팅만 집계합니다. (OpenAI 심각 분류 단독은 제외)
                </div>

                <div style={{ marginBottom: 24, background: S.white, borderRadius: 14, padding: '18px 20px', border: '0.5px solid ' + S.gray200 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: S.gray900, marginBottom: 6 }}>불법 키워드 관리</div>
                  <div style={{ fontSize: 12, color: S.gray400, marginBottom: 14, lineHeight: 1.5 }}>
                    등록 문구가 채팅에 포함되면 즉시 24시간 채팅 제한이 적용됩니다.
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}>
                    <input
                      type="text"
                      value={newIllegalPhrase}
                      onChange={(e) => setNewIllegalPhrase(e.target.value)}
                      placeholder="키워드 추가 (예: 마약, 대마)"
                      style={{ flex: '1 1 220px', minWidth: 0, padding: '10px 12px', borderRadius: 10, border: '1px solid ' + S.gray200, fontSize: 13, fontFamily: 'inherit' }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddIllegalKeyword(); }}
                    />
                    <button
                      type="button"
                      onClick={() => void handleAddIllegalKeyword()}
                      style={{ padding: '10px 16px', background: S.navy, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 800, fontSize: 13, fontFamily: 'inherit' }}
                    >
                      추가
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(illegalKeywordsList || []).length === 0 ? (
                      <span style={{ fontSize: 12, color: S.gray400 }}>등록된 키워드가 없습니다.</span>
                    ) : (
                      (illegalKeywordsList || []).map((row) => (
                        <div
                          key={String(row.id)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 10px',
                            borderRadius: 999,
                            background: row.is_active ? '#FEF2F2' : S.gray100,
                            border: '1px solid ' + (row.is_active ? '#FECACA' : S.gray200),
                            fontSize: 12,
                            fontWeight: 700,
                            color: row.is_active ? '#991B1B' : S.gray600,
                          }}
                        >
                          <span>{String(row.phrase || '').trim()}</span>
                          <button
                            type="button"
                            onClick={() => handleToggleIllegalKeyword(row.id, !row.is_active)}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, fontWeight: 800, color: S.gray600, fontFamily: 'inherit' }}
                          >
                            {row.is_active ? '끄기' : '켜기'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteIllegalKeyword(row.id)}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, fontWeight: 800, color: '#B91C1C', fontFamily: 'inherit' }}
                          >
                            삭제
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div style={{ fontSize: 16, fontWeight: 800, color: S.gray900, marginBottom: 12 }}>불법·조건만남 누적 사용자</div>
                {illegalRiskUsers.length === 0 ? (
                  <div style={{ background: S.white, borderRadius: 14, padding: '40px', textAlign: 'center', color: S.gray400, border: '0.5px solid ' + S.gray200 }}>
                    현재 해당 위험 사용자가 없어요
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {illegalRiskUsers.map((u) => {
                      const rec = String(u?.recommendation || '');
                      const isHandled = !Array.isArray(u?.pending_reviews) || u.pending_reviews.length === 0;
                      const recText = rec === 'ACCOUNT_SUSPEND_7D_RECOMMEND'
                        ? '7일 정지 추천'
                        : rec === 'PERMANENT_BAN_REVIEW'
                          ? '영구정지 추천'
                          : rec === 'ACCOUNT_SUSPEND_1D_RECOMMEND'
                            ? '1일 정지 추천'
                            : rec === 'CHAT_BAN_24H_REVIEW'
                              ? '24시간 채팅 제한'
                              : rec === 'CHAT_BAN_10M_REVIEW'
                                ? '24시간 채팅 제한'
                                : '불법 유저';
                      return (
                        <div key={String(u?.user_id || '')} style={{ background: S.white, borderRadius: 12, padding: '16px 18px', border: '0.5px solid ' + S.gray200 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: S.gray900 }}>
                              {String(u?.display_name || '').trim() || displayUserById(u?.user_id)}
                              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, color: '#B91C1C', background: '#FEE2E2', padding: '2px 8px', borderRadius: 6 }}>
                                {recText}
                              </span>
                              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 800, color: isHandled ? '#6B7280' : '#92400E', background: isHandled ? '#FFFFFF' : '#FEF3C7', border: isHandled ? '1px solid #E5E7EB' : '1px solid #FDE68A', padding: '2px 8px', borderRadius: 6 }}>
                                {isHandled ? '처리완료' : '미처리'}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: S.gray400 }}>{String(u?.user_id || '').slice(0, 8)}…</div>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, background: S.gray100, color: S.gray700, padding: '2px 8px', borderRadius: 6 }}>전체 신고(30일) {Number(u?.report_count_30d ?? u?.global_report_count ?? 0)}건</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: S.gray100, color: S.gray700, padding: '2px 8px', borderRadius: 6 }}>욕설 누적(7일) {Number(u?.ai_flag_count_7d ?? u?.monthly_profanity_count ?? 0)}회</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: '#FFEDD5', color: '#9A3412', padding: '2px 8px', borderRadius: 6 }}>불법·조건만남(7일) {Number(u?.illegal_flag_count_7d ?? 0)}회</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: '#EEF2FF', color: '#3730A3', padding: '2px 8px', borderRadius: 6 }}>영구누적 신고(전체) {Number(u?.total_report_count ?? 0)}건</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: '#F5F3FF', color: '#6D28D9', padding: '2px 8px', borderRadius: 6 }}>영구누적 욕설(전체) {Number(u?.total_ai_flag_count ?? 0)}회</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: '#FED7AA', color: '#C2410C', padding: '2px 8px', borderRadius: 6 }}>누적 불법(전체) {Number(u?.total_illegal_flag_count ?? 0)}회</span>
                          </div>
                          {Array.isArray(u?.recent_illegal_events) && u.recent_illegal_events.length > 0 && (
                            <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid ' + S.gray200, background: S.gray50 }}>
                              <div style={{ fontSize: 12, fontWeight: 900, color: S.gray900, marginBottom: 6 }}>최근 탐지 내역</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {u.recent_illegal_events.slice(0, 4).map((ev, idx) => {
                                  const t = ev?.created_at ? new Date(ev.created_at) : null;
                                  const timeStr = t && !Number.isNaN(t.getTime())
                                    ? t.toLocaleString('ko-KR', { hour12: false })
                                    : '-';
                                  const reasonTag = String(ev?.severe_reason || '') === 'illegal_admin_keyword'
                                    ? 'DB키워드'
                                    : String(ev?.severe_reason || '') === 'conditional_meet'
                                      ? '조건만남'
                                      : String(ev?.severe_reason || '') === 'openai_serious'
                                        ? 'AI심각'
                                        : '불법';
                                  return (
                                    <div key={String(ev?.created_at || idx)} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                      <span style={{ fontSize: 10, fontWeight: 900, color: '#7C2D12', background: '#FFEDD5', padding: '1px 6px', borderRadius: 4, marginTop: 2, flex: '0 0 auto' }}>{reasonTag}</span>
                                      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                                        <div style={{ fontSize: 12, color: S.gray900, wordBreak: 'break-word' }}>
                                          {String(ev?.input_preview || '').trim() || '-'}
                                        </div>
                                        <div style={{ fontSize: 11, color: S.gray500, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                          <span style={{ fontWeight: 800, color: S.gray600 }}>{String(ev?.room_title || '').trim() || '(방 미상)'}</span>
                                          <span>{timeStr}</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {!isHandled && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleWarnFromProfanityRisk(u)}
                                  style={{ padding: '8px 14px', background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}
                                >
                                  ⚠️ 경고
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleBanFromIllegalRisk(u, '1d')}
                                  style={{ padding: '8px 14px', background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}
                                >
                                  1일 정지
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleBanFromIllegalRisk(u, '7d')}
                                  style={{ padding: '8px 14px', background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FECACA', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}
                                >
                                  7일 정지
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleBanFromIllegalRisk(u, 'permanent')}
                                  style={{ padding: '8px 14px', background: '#EDE9FE', color: '#5B21B6', border: '1px solid #DDD6FE', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}
                                >
                                  영구정지
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              onClick={() => u?.active_sanctions?.CHAT_BAN_10M ? handleReleaseUserSanctionsV2(u, ['CHAT_BAN_10M']) : handleApplyUserSanctionV2(u, 'CHAT_BAN_10M')}
                              style={sanctionToggleBtnStyle(Boolean(u?.active_sanctions?.CHAT_BAN_10M), 'blue')}
                            >
                              10분 챗밴 {u?.active_sanctions?.CHAT_BAN_10M ? 'ON' : 'OFF'}
                            </button>
                            <button
                              type="button"
                              onClick={() => u?.active_sanctions?.CHAT_BAN_24H ? handleReleaseUserSanctionsV2(u, ['CHAT_BAN_24H']) : handleApplyUserSanctionV2(u, 'CHAT_BAN_24H')}
                              style={sanctionToggleBtnStyle(Boolean(u?.active_sanctions?.CHAT_BAN_24H), 'blue')}
                            >
                              24시간 챗밴 {u?.active_sanctions?.CHAT_BAN_24H ? 'ON' : 'OFF'}
                            </button>
                            <button
                              type="button"
                              onClick={() => u?.active_sanctions?.ACCOUNT_SUSPEND_1D ? handleReleaseUserSanctionsV2(u, ['ACCOUNT_SUSPEND_1D']) : handleApplyUserSanctionV2(u, 'ACCOUNT_SUSPEND_1D')}
                              style={sanctionToggleBtnStyle(Boolean(u?.active_sanctions?.ACCOUNT_SUSPEND_1D), 'green')}
                            >
                              1일 정지 {u?.active_sanctions?.ACCOUNT_SUSPEND_1D ? 'ON' : 'OFF'}
                            </button>
                            <button
                              type="button"
                              onClick={() => u?.active_sanctions?.ACCOUNT_SUSPEND_7D ? handleReleaseUserSanctionsV2(u, ['ACCOUNT_SUSPEND_7D']) : handleApplyUserSanctionV2(u, 'ACCOUNT_SUSPEND_7D')}
                              style={sanctionToggleBtnStyle(Boolean(u?.active_sanctions?.ACCOUNT_SUSPEND_7D), 'green')}
                            >
                              7일 정지 {u?.active_sanctions?.ACCOUNT_SUSPEND_7D ? 'ON' : 'OFF'}
                            </button>
                            <button
                              type="button"
                              onClick={() => u?.active_sanctions?.PERMANENT_BAN ? handleReleaseUserSanctionsV2(u, ['PERMANENT_BAN']) : handleApplyUserSanctionV2(u, 'PERMANENT_BAN')}
                              style={sanctionToggleBtnStyle(Boolean(u?.active_sanctions?.PERMANENT_BAN), 'red')}
                            >
                              영구정지 {u?.active_sanctions?.PERMANENT_BAN ? 'ON' : 'OFF'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleResetIllegalCount(u)}
                              style={{ padding: '8px 14px', background: S.gray100, color: S.gray700, border: '1px solid ' + S.gray200, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}
                            >
                              불법누적(7일) 초기화
                            </button>
                            <button
                              type="button"
                              onClick={() => handleResetTotalIllegalCount(u)}
                              style={{ padding: '8px 14px', background: '#F3F4F6', color: S.gray900, border: '1px solid ' + S.gray200, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 900, fontFamily: 'inherit' }}
                            >
                              불법누적(전체) 초기화
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === 'banned' && (
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: S.gray900, marginBottom: 8 }}>🚫 정지 유저</div>
                <div style={{ fontSize: 13, color: S.gray400, marginBottom: 16, lineHeight: 1.6 }}>
                  계정 정지(1일 · 7일 · 영구) 목록입니다. 아래 카드만 사용합니다.
                </div>

                <div style={{ background: S.white, borderRadius: 14, padding: 16, border: '0.5px solid ' + S.gray200 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: S.gray900, marginBottom: 10 }}>🧊 계정 정지(1일/7일/영구)</div>
                  {accountSuspendedUsersV2.length === 0 ? (
                    <div style={{ color: S.gray400, fontSize: 13, padding: '10px 0' }}>현재 계정 정지 사용자가 없어요</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {accountSuspendedUsersV2.map((r, idx) => (
                        <div key={String(r?.id || idx)} style={{ border: '1px solid ' + S.gray200, borderRadius: 12, padding: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                            <div style={{ fontSize: 14, fontWeight: 900, color: S.gray900 }}>
                              {String(r?.display_name || '사용자')}
                              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 900, background: '#EDE9FE', color: '#5B21B6', padding: '2px 8px', borderRadius: 6 }}>
                                {String(r?.sanction_type || '') === 'ACCOUNT_SUSPEND_1D'
                                  ? '1일정지'
                                  : String(r?.sanction_type || '') === 'ACCOUNT_SUSPEND_7D'
                                    ? '7일정지'
                                    : String(r?.sanction_type || '') === 'PERMANENT_BAN'
                                      ? '영구정지'
                                      : String(r?.sanction_type || '')}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: S.gray400, fontFamily: 'monospace' }}>
                              {String(r?.user_id || '').slice(0, 8)}…
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.9fr 0.9fr', gap: 10, fontSize: 12, color: S.gray700, marginBottom: 10 }}>
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                              <span style={{ fontWeight: 900, color: S.gray900 }}>정지 사유</span>
                              <div>{String(r?.reason || '-')}</div>
                            </div>
                            <div>
                              <span style={{ fontWeight: 900, color: S.gray900 }}>정지 시작</span>
                              <div>{String(r?.start_at || '').slice(0, 19).replace('T', ' ')}</div>
                            </div>
                            <div>
                              <span style={{ fontWeight: 900, color: S.gray900 }}>정지 종료</span>
                              <div>{r?.end_at ? String(r.end_at).slice(0, 19).replace('T', ' ') : '없음(무기한)'}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={() => handleReleaseUserSanctionsV2(r, [String(r?.sanction_type || '')])}
                              style={{ padding: '8px 14px', background: '#ECFDF5', color: '#166534', border: '1px solid #BBF7D0', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 900, fontFamily: 'inherit' }}
                            >
                              정지 해제
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteAccountSuspensionV2(r)}
                              style={{ padding: '8px 14px', background: S.gray100, color: S.gray700, border: '1px solid ' + S.gray200, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 900, fontFamily: 'inherit' }}
                            >
                              데이터 삭제
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'sanction_release_logs' && (
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: S.gray900, marginBottom: 8 }}>🧾 정지/제재 해제 사유</div>
                <div style={{ fontSize: 13, color: S.gray400, marginBottom: 16, lineHeight: 1.6 }}>
                  관리자 제재 적용·해제 기록입니다. (사용자·적용/해제 사유·제재 종류·일시)
                </div>
                {sanctionReleaseLogs.length === 0 ? (
                  <div style={{ background: S.white, borderRadius: 14, padding: '40px', textAlign: 'center', color: S.gray400, border: '0.5px solid ' + S.gray200 }}>
                    기록이 없어요
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {sanctionReleaseLogs.map((r, idx) => (
                      <div key={String(r?.id || idx)} style={{ background: S.white, borderRadius: 12, padding: '14px 16px', border: '0.5px solid ' + S.gray200 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                          <div style={{ fontSize: 14, fontWeight: 900, color: S.gray900 }}>
                            {String(r?.target_display_name || '사용자')}
                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, color: '#6B7280', background: S.gray100, padding: '2px 8px', borderRadius: 6 }}>
                              {Array.isArray(r?.release_types) ? r.release_types.join(', ') : String(r?.release_types || '-')}
                            </span>
                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 900, color: String(r?.log_action || '') === 'apply' ? '#1D4ED8' : '#166534', background: String(r?.log_action || '') === 'apply' ? '#EFF6FF' : '#ECFDF5', padding: '2px 8px', borderRadius: 6 }}>
                              {String(r?.log_action || '') === 'apply' ? '적용' : '해제'}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: S.gray400 }}>
                            {String(r?.created_at || '').slice(0, 19).replace('T', ' ')}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: S.gray700, lineHeight: 1.6, marginBottom: 10, whiteSpace: 'pre-wrap' }}>
                          <div style={{ fontWeight: 800, color: S.gray900, marginBottom: 4 }}>
                            {String(r?.log_action || '') === 'apply' ? '적용 사유' : '해제 사유'}
                          </div>
                          {String(r?.reason || '-')}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!window.confirm('이 기록을 삭제할까요?')) return;
                              try {
                                const res = await adminApi(verifiedPin, 'delete_sanction_release_log_v2', { id: r?.id });
                                if (res?.success === false) throw new Error(res?.error || 'delete failed');
                                await reload('sanction_release_logs');
                                showNotif('기록을 삭제했습니다.');
                              } catch (e) {
                                logAdminUi('delete_sanction_release_log_v2', e?.message, e);
                                showNotif(e?.message || '삭제에 실패했습니다.', 'error');
                              }
                            }}
                            style={{ padding: '8px 14px', background: S.gray100, color: S.gray700, border: '1px solid ' + S.gray200, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 800, fontFamily: 'inherit' }}
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {false && tab==='admin_actions' && (
              <div>
                <div style={{fontSize:22,fontWeight:800,color:S.gray900,marginBottom:8}}>🧾 감사 로그</div>
                <div style={{fontSize:13,color:S.gray400,marginBottom:20}}>관리자 제재/해제/카운트 초기화 이력을 확인합니다.</div>
                {adminActions.length === 0 ? (
                  <div style={{background:S.white,borderRadius:14,padding:'30px',textAlign:'center',color:S.gray400,border:'0.5px solid '+S.gray200}}>로그가 없어요</div>
                ) : (
                  <div style={{background:S.white,borderRadius:14,border:'0.5px solid '+S.gray200,overflow:'hidden'}}>
                    <div style={{display:'grid',gridTemplateColumns:'170px 130px 1fr 1fr',padding:'10px 14px',fontSize:12,fontWeight:800,color:S.gray600,background:S.gray50,borderBottom:'1px solid '+S.gray200}}>
                      <div>시간</div><div>액션</div><div>사용자</div><div>상세</div>
                    </div>
                    {(adminActions || []).map((a, i) => (
                      <div key={String(a?.id || i)} style={{display:'grid',gridTemplateColumns:'170px 130px 1fr 1fr',padding:'10px 14px',fontSize:12,color:S.gray700,borderBottom:i===adminActions.length-1?'none':'1px solid '+S.gray200}}>
                        <div>{String(a?.created_at || '').slice(0,19).replace('T',' ')}</div>
                        <div>{String(a?.action_type || '-')}</div>
                        <div>{String(a?.display_name || renderUserNameOnly(a?.target_user_id, a?.target_profile))}</div>
                        <div>{String(a?.payload?.reset_reason || a?.payload?.reason || '-')}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── 공지사항 ── */}
            {tab==='notices' && (
              <div>
                <div style={{fontSize:22,fontWeight:800,color:S.gray900,marginBottom:20}}>📢 공지사항 관리</div>
                <div style={{background:S.white,borderRadius:14,padding:24,border:'0.5px solid '+S.gray200,marginBottom:24}}>
                  <div style={{fontSize:15,fontWeight:700,color:S.gray900,marginBottom:16}}>새 공지 작성</div>
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:12,fontWeight:600,color:S.gray600,marginBottom:6}}>제목</div>
                    <input value={noticeTitle} onChange={e => setNoticeTitle(e.target.value)} placeholder="공지 제목 입력..."
                      style={{width:'100%',padding:'10px 14px',border:'1px solid '+S.gray200,borderRadius:8,fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                  </div>
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:12,fontWeight:600,color:S.gray600,marginBottom:6}}>내용 (선택)</div>
                    <textarea value={noticeContent} onChange={e => setNoticeContent(e.target.value)} placeholder="공지 내용..." rows={3}
                      style={{width:'100%',padding:'10px 14px',border:'1px solid '+S.gray200,borderRadius:8,fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box',resize:'vertical'}}/>
                  </div>
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:12,fontWeight:600,color:S.gray600,marginBottom:6}}>대상</div>
                    <div style={{display:'flex',gap:8}}>
                      {['전체','강남구','성동구'].map(t => (
                        <button key={t} onClick={() => setNoticeTarget(t)}
                          style={{padding:'7px 16px',background:noticeTarget===t?S.greenLight:S.gray100,color:noticeTarget===t?S.green:S.gray600,border:noticeTarget===t?'1.5px solid '+S.green:'1.5px solid '+S.gray200,borderRadius:7,cursor:'pointer',fontSize:13,fontFamily:'inherit',fontWeight:700}}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button onClick={handleAddNotice}
                    style={{padding:'12px 28px',background:S.green,color:'white',border:'none',borderRadius:10,cursor:'pointer',fontSize:14,fontWeight:700,fontFamily:'inherit'}}>
                    📢 공지 발송
                  </button>
                  <div style={{fontSize:12,color:S.gray400,marginTop:8}}>* 발송 시 앱 홈 상단 배너에 표시됩니다. 기존 공지는 자동 비활성화됩니다.</div>
                </div>

                {notices.length===0
                  ? <div style={{background:S.white,borderRadius:14,padding:'30px',textAlign:'center',color:S.gray400,border:'0.5px solid '+S.gray200}}>작성된 공지사항이 없어요</div>
                  : notices.map(n => (
                    <div key={n.id} style={{background:S.white,borderRadius:12,padding:'16px 20px',marginBottom:10,border:'0.5px solid '+S.gray200,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <div style={{flex:1}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                          <span style={{fontSize:14,fontWeight:700,color:S.gray900}}>{n.title}</span>
                          {n.active && <span style={{background:S.greenLight,color:S.green,fontSize:11,fontWeight:700,padding:'2px 7px',borderRadius:4}}>앱 표시중</span>}
                        </div>
                        <div style={{fontSize:12,color:S.gray400}}>{n.target} · {n.created_at?.slice(0,10)}</div>
                        {n.content && <div style={{fontSize:13,color:S.gray600,marginTop:4}}>{n.content}</div>}
                      </div>
                      <div style={{display:'flex',gap:8,marginLeft:16}}>
                        <button onClick={() => handleToggleNotice(n.id)}
                          style={{padding:'6px 12px',background:n.active?S.yellowLight:S.greenLight,color:n.active?S.yellow:S.green,border:'none',borderRadius:7,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>
                          {n.active?'숨기기':'활성화'}
                        </button>
                        <button onClick={() => handleDeleteNotice(n.id)}
                          style={{padding:'6px 12px',background:S.redLight,color:S.red,border:'none',borderRadius:7,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>삭제</button>
                      </div>
                    </div>
                  ))
                }
              </div>
            )}

            {/* ── 포인트 관리 ── */}
            {tab==='coins' && (
              <div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
                  <div>
                    <div style={{fontSize:22,fontWeight:800,color:S.gray900}}>🪙 포인트 관리</div>
                    <div style={{fontSize:13,color:S.gray400,marginTop:4}}>회원별 포인트 조회 · 지급 · 차감</div>
                  </div>
                  <button onClick={() => reload('coins')}
                    style={{padding:'9px 18px',background:S.gray100,color:S.gray600,border:'none',borderRadius:9,cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit'}}>
                    🔄 새로고침
                  </button>
                </div>
                <div style={{background:'#FEF3C7',borderRadius:10,padding:'12px 16px',marginBottom:16,border:'0.5px solid #FCD34D',fontSize:13,color:'#92400E'}}>
                  ⚠️ 이 탭은 Edge Function에 <strong>get_users_coins</strong>, <strong>adjust_coins</strong> 액션 추가 후 동작합니다.<br/>
                  App.js 상단 주석 블록의 Edge Function 코드 가이드를 참고하세요.
                </div>
                {coinUsers.length === 0
                  ? <div style={{background:S.white,borderRadius:14,padding:'48px',textAlign:'center',color:S.gray400,border:'0.5px solid '+S.gray200}}>
                      포인트 데이터가 없어요.<br/>새로고침 버튼을 눌러주세요.
                    </div>
                  : <div style={{background:S.white,borderRadius:14,border:'0.5px solid '+S.gray200,overflow:'auto'}}>
                      <table style={{width:'100%',borderCollapse:'collapse',minWidth:700}}>
                        <thead>
                          <tr style={{background:S.gray50,borderBottom:'1px solid '+S.gray200}}>
                            {['이름','만 나이','전화번호','무료 포인트','유료 포인트','지급/차감'].map(h => (
                              <th key={h} style={{padding:'12px 14px',textAlign:'left',fontSize:12,fontWeight:700,color:S.gray400,whiteSpace:'nowrap'}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {coinUsers.map(u => (
                            <tr key={u.user_id} style={{borderBottom:'0.5px solid '+S.gray100}}>
                              <td style={{padding:'12px 14px',fontSize:14,fontWeight:600,color:S.gray900}}>{String(u.display_name || u.name || '-')}</td>
                              <td style={{padding:'12px 14px',fontSize:13,color:S.gray600,whiteSpace:'nowrap'}}>
                                {u.man_age != null ? `만 ${u.man_age}세` : '-'}
                              </td>
                              <td style={{padding:'12px 14px',fontSize:13,color:S.gray600}}>{u.phone?.replace('+82','0')||'-'}</td>
                              <td style={{padding:'12px 14px',fontSize:15,fontWeight:700,color:S.green}}>{u.free_coins??0} 🪙</td>
                              <td style={{padding:'12px 14px',fontSize:15,fontWeight:700,color:S.blue}}>{u.paid_coins??0} 🪙</td>
                              <td style={{padding:'10px 14px'}}>
                                <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
                                  <select
                                    value={coinInputs[u.user_id]?.coinType||'paid'}
                                    onChange={e => setCoinInputs(prev => ({...prev,[u.user_id]:{...prev[u.user_id],coinType:e.target.value}}))}
                                    style={{width:72,padding:'6px 7px',border:'1px solid '+S.gray200,borderRadius:6,fontSize:13,fontFamily:'inherit',outline:'none',background:S.white}}>
                                    <option value="paid">유료</option>
                                    <option value="free">무료</option>
                                  </select>
                                  <input type="number" min="1" placeholder="수량"
                                    value={coinInputs[u.user_id]?.amount||''}
                                    onChange={e => setCoinInputs(prev => ({...prev,[u.user_id]:{...prev[u.user_id],amount:e.target.value}}))}
                                    style={{width:70,padding:'6px 9px',border:'1px solid '+S.gray200,borderRadius:6,fontSize:13,fontFamily:'inherit',outline:'none'}}/>
                                  <input type="text" placeholder="사유"
                                    value={coinInputs[u.user_id]?.reason||''}
                                    onChange={e => setCoinInputs(prev => ({...prev,[u.user_id]:{...prev[u.user_id],reason:e.target.value}}))}
                                    style={{width:100,padding:'6px 9px',border:'1px solid '+S.gray200,borderRadius:6,fontSize:13,fontFamily:'inherit',outline:'none'}}/>
                                  <button
                                    onClick={() => handleCoinAdjust(u.user_id, +(coinInputs[u.user_id]?.amount||0), coinInputs[u.user_id]?.reason, coinInputs[u.user_id]?.coinType||'paid')}
                                    style={{padding:'6px 11px',background:S.greenLight,color:S.green,border:'none',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit',whiteSpace:'nowrap'}}>
                                    + 지급
                                  </button>
                                  <button
                                    onClick={() => handleCoinAdjust(u.user_id, -(coinInputs[u.user_id]?.amount||0), coinInputs[u.user_id]?.reason, coinInputs[u.user_id]?.coinType||'paid')}
                                    style={{padding:'6px 11px',background:S.redLight,color:S.red,border:'none',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit',whiteSpace:'nowrap'}}>
                                    − 차감
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                }
              </div>
            )}

            {/* ── 채팅 금지 사용자 ── */}
            {false && tab==='chat_bans' && (
              <div>
                <div style={{fontSize:22,fontWeight:800,color:S.gray900,marginBottom:8}}>💬 채팅 금지 사용자</div>
                <div style={{fontSize:13,color:S.gray400,marginBottom:20}}>chat_ban_10m / chat_ban_24h 상태를 관리자가 즉시 해제할 수 있습니다.</div>
                {chatBannedUsers.length===0
                  ? <div style={{background:S.white,borderRadius:14,padding:'30px',textAlign:'center',color:S.gray400,border:'0.5px solid '+S.gray200}}>현재 채팅 금지 사용자가 없어요</div>
                  : <div style={{background:S.white,borderRadius:14,border:'0.5px solid '+S.gray200,overflow:'hidden'}}>
                      <table style={{width:'100%',borderCollapse:'collapse'}}>
                        <thead>
                          <tr style={{background:S.gray50,borderBottom:'1px solid '+S.gray200}}>
                            {['사용자','제재 유형','금지된 방','사유','시작','종료','해제'].map(h => (
                              <th key={h} style={{padding:'12px 14px',textAlign:'left',fontSize:12,fontWeight:700,color:S.gray400}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {chatBannedUsers.map((r) => (
                            <tr key={r.id} style={{borderBottom:'0.5px solid '+S.gray100}}>
                              <td style={{padding:'12px 14px',fontSize:13,color:S.gray900,fontWeight:600}}>
                                {String(r?.display_name || renderUserNameOnly(r.user_id, r.user_profile))}
                              </td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray700}}>{r.sanction_type}</td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray700}}>
                                {r.room_info?.title || r.room_id || '-'}
                              </td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray600}}>{r.reason || '-'}</td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray500}}>{r.start_at?.slice(0,16)?.replace('T',' ')}</td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray500}}>{r.end_at ? r.end_at.slice(0,16).replace('T',' ') : '무기한'}</td>
                              <td style={{padding:'12px 14px'}}>
                                <button onClick={() => handleReleaseChatBan(r)}
                                  style={{padding:'5px 12px',background:S.greenLight,color:S.green,border:'none',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>
                                  해제
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                }
              </div>
            )}

            {/* ── 채팅방 강퇴 사용자 ── */}
            {false && tab==='room_kicks_manage' && (
              <div>
                <div style={{fontSize:22,fontWeight:800,color:S.gray900,marginBottom:8}}>🚪 채팅방 강퇴 사용자</div>
                <div style={{fontSize:13,color:S.gray400,marginBottom:20}}>ROOM_KICK / club_kicks 기록을 방 단위로 해제할 수 있습니다.</div>
                {roomKickedUsers.length===0
                  ? <div style={{background:S.white,borderRadius:14,padding:'30px',textAlign:'center',color:S.gray400,border:'0.5px solid '+S.gray200}}>현재 강퇴된 사용자가 없어요</div>
                  : <div style={{background:S.white,borderRadius:14,border:'0.5px solid '+S.gray200,overflow:'hidden'}}>
                      <table style={{width:'100%',borderCollapse:'collapse'}}>
                        <thead>
                          <tr style={{background:S.gray50,borderBottom:'1px solid '+S.gray200}}>
                            {['사용자','강퇴된 방','출처','사유','강퇴일','해제'].map(h => (
                              <th key={h} style={{padding:'12px 14px',textAlign:'left',fontSize:12,fontWeight:700,color:S.gray400}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {roomKickedUsers.map((k, i) => (
                            <tr key={`${k.club_id}_${k.user_id}_${k.created_at || i}`} style={{borderBottom:'0.5px solid '+S.gray100}}>
                              <td style={{padding:'12px 14px',fontSize:13,color:S.gray900,fontWeight:600}}>
                                {String(k?.display_name || renderUserNameOnly(k.user_id, k.user_profile))}
                              </td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray700}}>
                                {k.room_info?.title || k.club_id || '-'}
                              </td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray700}}>{k.kick_source || '-'}</td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray600}}>{k.kick_detail || k.reason || '-'}</td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray500}}>{k.created_at?.slice(0,16)?.replace('T',' ')}</td>
                              <td style={{padding:'12px 14px'}}>
                                <button onClick={() => handleReleaseRoomKick(k)}
                                  style={{padding:'5px 12px',background:S.greenLight,color:S.green,border:'none',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>
                                  해제
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                }
              </div>
            )}

            {/* ── 계정 정지 사용자(제재 테이블 기준) ── */}
            {false && tab==='account_suspensions' && (
              <div>
                <div style={{fontSize:22,fontWeight:800,color:S.gray900,marginBottom:8}}>🧊 계정 정지 사용자</div>
                <div style={{fontSize:13,color:S.gray400,marginBottom:20}}>ACCOUNT_SUSPEND_1D / ACCOUNT_SUSPEND_7D / PERMANENT_BAN 제재를 해제합니다.</div>
                {accountSuspendedUsers.length===0
                  ? <div style={{background:S.white,borderRadius:14,padding:'30px',textAlign:'center',color:S.gray400,border:'0.5px solid '+S.gray200}}>현재 계정 정지 사용자가 없어요</div>
                  : <div style={{background:S.white,borderRadius:14,border:'0.5px solid '+S.gray200,overflow:'hidden'}}>
                      <table style={{width:'100%',borderCollapse:'collapse'}}>
                        <thead>
                          <tr style={{background:S.gray50,borderBottom:'1px solid '+S.gray200}}>
                            {['사용자','정지 유형','사유','시작','종료','해제'].map(h => (
                              <th key={h} style={{padding:'12px 14px',textAlign:'left',fontSize:12,fontWeight:700,color:S.gray400}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {accountSuspendedUsers.map((r) => (
                            <tr key={r.id} style={{borderBottom:'0.5px solid '+S.gray100}}>
                              <td style={{padding:'12px 14px',fontSize:13,color:S.gray900,fontWeight:600}}>
                                {String(r?.display_name || renderUserNameOnly(r.user_id, r.user_profile))}
                              </td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray700}}>{r.sanction_type}</td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray600}}>{r.reason || '-'}</td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray500}}>{r.start_at?.slice(0,16)?.replace('T',' ')}</td>
                              <td style={{padding:'12px 14px',fontSize:12,color:S.gray500}}>{r.end_at ? r.end_at.slice(0,16).replace('T',' ') : '무기한'}</td>
                              <td style={{padding:'12px 14px'}}>
                                <button onClick={() => handleReleaseAccountSuspension(r)}
                                  style={{padding:'5px 12px',background:S.greenLight,color:S.green,border:'none',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>
                                  해제
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                }
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

function PinScreen({ pin, setPin, error, loading, onSubmit }) {
  return (
    <div style={{minHeight:'100vh',background:'#0F1923',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:'#1A2B3C',borderRadius:20,padding:'40px 36px',width:320,textAlign:'center',boxShadow:'0 20px 60px rgba(0,0,0,0.4)'}}>
        <div style={{fontSize:32,marginBottom:8}}>🔐</div>
        <div style={{fontSize:20,fontWeight:800,color:'white',marginBottom:4}}>관리자 로그인</div>
        <div style={{fontSize:13,color:'rgba(255,255,255,0.4)',marginBottom:28}}>Supabase Edge Function 인증</div>
        <input type="password" value={pin} onChange={e => setPin(e.target.value)}
          onKeyDown={e => e.key==='Enter' && onSubmit()}
          placeholder="PIN 번호 입력"
          style={{width:'100%',padding:'13px 16px',background:'rgba(255,255,255,0.08)',
            border:error?'1.5px solid #EF4444':'1.5px solid rgba(255,255,255,0.1)',
            borderRadius:10,fontSize:18,color:'white',textAlign:'center',
            letterSpacing:8,fontFamily:'inherit',outline:'none',boxSizing:'border-box',
            marginBottom:error?8:16}}/>
        {error ? <div style={{color:'#EF4444',fontSize:13,marginBottom:12,lineHeight:1.5}}>{error}</div> : null}
        <button onClick={onSubmit} disabled={loading}
          style={{width:'100%',padding:'13px',background:loading?'#1a6e42':'#2E9B64',color:'white',border:'none',borderRadius:10,cursor:'pointer',fontSize:15,fontWeight:700,fontFamily:'inherit'}}>
          {loading?'인증 중...':'입장하기'}
        </button>
      </div>
    </div>
  );
}